<template>
  <div ref="rootEl" class="datatable-container" :class="{ 'fill-parent-mode': config.heightMode === 'fill-parent' }">
    <!-- Action Buttons Toolbar -->
    <div v-if="showToolbar" class="datatable-toolbar">
      <!-- Left side - Refresh Button + Add Button -->
      <div class="toolbar-left">
        <Button
          v-if="refreshButton && refreshButton.show"
          :severity="refreshButton.severity || 'secondary'"
          :data-testid="testIdScope('refresh-btn')"
          outlined
          size="small"
          :label="refreshButton.text || t('Atualizar')"
          icon="pi pi-refresh"
          :loading="isLoadingData"
          @click="handleRefresh"
        />
        <Button
          v-if="isInlineEditing && config.inlineAddButton !== false"
          :data-testid="testIdScope('inline-add-btn')"
          severity="success"
          outlined
          size="small"
          :label="config.inlineAddLabel || t('Adicionar')"
          icon="pi pi-plus"
          @click="addNewRow"
        />
      </div>

      <!-- Right side - Export Buttons -->
      <div class="toolbar-right">
        <Button
          v-if="csvButton"
          :data-testid="testIdScope('csv-btn')"
          severity="secondary"
          size="small"
          :label="csvButton.text || t('CSV')"
          icon="pi pi-file"
          @click="exportCSV"
        />
        <Button
          v-if="csvButton"
          :data-testid="testIdScope('xlsx-btn')"
          severity="secondary"
          size="small"
          :label="t('Excel')"
          icon="pi pi-file-excel"
          @click="exportXLSX"
        />
      </div>
    </div>

    <!-- PrimeVue DataTable (Table Mode) -->
    <PrimeDataTable
      v-if="effectiveDisplayMode === 'table'"
      ref="datatableRef"
      v-model:selection="selectedRows"
      v-model:filters="filters"
      :value="displayData"
      :frozenValue="frozenFooterRows"
      :loading="isLoadingData"
      :paginator="config.pagination === true"
      :rows="config.pageLength || 100"
      :rowsPerPageOptions="[10, 25, 50, 100]"
      :dataKey="getDataKey()"
      :selectionMode="getSelectionMode()"
      :rowClass="getRowClass"
      :pt="primeDataTablePt"
      :sortMode="isTableSortable ? 'multiple' : undefined"
      v-model:multiSortMeta="multiSortMeta"
      :resizableColumns="config.resizableColumns !== false"
      :columnResizeMode="config.columnResizeMode || 'expand'"
      :filterDisplay="shouldShowFilters ? (config.filterDisplay || 'row') : undefined"
      :stripedRows="config.stripedRows !== false"
      :showGridlines="config.showGridlines !== false"
      :size="config.size || 'small'"
      :scrollable="true"
      :scrollHeight="computedScrollHeight"
      :virtualScrollerOptions="config.virtualScroll ? getVirtualScrollOptions() : undefined"
      responsiveLayout="scroll"
      :isDataSelectable="props.isDataSelectable"
      @rowSelect="onRowSelect"
      @rowClick="onRowClick"
      @rowDblclick="onRowDblClick"
      @filter="onFilter"
      class="p-datatable-sm">

      <!-- Checkbox Selection Column (position: first) -->
      <Column
        v-if="hasCheckboxSelection && checkboxConfig.position !== 'last'"
        selectionMode="multiple"
        :frozen="checkboxConfig.frozen"
        :headerStyle="{ width: checkboxConfig.width }"
        :bodyStyle="{ textAlign: 'center' }">
      </Column>

      <!-- Dynamic Columns -->
      <Column
        v-for="(column, index) in config.columns"
        :key="column.data || `col_${index}`"
        :field="column.data || undefined"
        :filterField="column.mergedColumns?.length ? `__merged_${column.data}` : (column.filterField || undefined)"
        :sortField="column.sortField || undefined"
        :header="headerExplanation(column) ? undefined : column.title"
        :sortable="isColumnSortable(column)"
        :dataType="getColumnDataType(column)"
        :frozen="column.frozen === true"
        :alignFrozen="column.alignFrozen || 'left'"
        :style="getColumnStyle(column)"
        :bodyStyle="getColumnBodyStyle(column)">

        <!-- Header Template — only when the column explains itself (`explanation`,
             or the legacy `headerTooltip` string read as one) -->
        <template v-if="headerExplanation(column)" #header>
          <span class="header-with-info">
            <span>{{ column.title }}</span>
            <InfoTip
              :explanation="headerExplanation(column)"
              :label="column.title"
              :test-id="column.headerTooltipTestId || `dt-header-info-${column.data}`"
            />
          </span>
        </template>

        <!-- Body Template -->
        <template #body="slotProps">
          <!-- Special handling for frozen footer row -->
          <template v-if="slotProps.data._isFrozenFooter">
            <!-- Custom footer renderer if provided -->
            <strong v-if="column.footerRenderer">
              <div v-html="renderFooterCellSanitizado(column, slotProps.data)"></div>
            </strong>
            <strong v-else-if="column.data && column.footerType && column.footerType !== 'none' && slotProps.data[column.data] !== undefined">
              <span v-if="column.renderType === 'currency' || column.footerCurrency">
                {{ n(parseFloat(slotProps.data[column.data]) || 0, 'currency') }}
              </span>
              <span v-else-if="column.renderType === 'number'">
                {{ n(parseFloat(slotProps.data[column.data]) || 0, 'decimal') }}
              </span>
              <span v-else-if="column.renderType === 'integer'">
                {{ n(parseFloat(slotProps.data[column.data]) || 0, 'integer') }}
              </span>
              <span v-else-if="column.renderType === 'percentage'">
                {{ n((parseFloat(slotProps.data[column.data]) || 0) / 100, 'percent') }}
              </span>
              <span v-else>
                {{ slotProps.data[column.data] }}
              </span>
            </strong>
          </template>
          
          <!-- Regular row rendering (wrapped in the row's own explanation, when the column has one) -->
          <ExplainedCell v-else :explanation="column.cellExplanation?.(slotProps.data) ?? null">
            <!-- INLINE EDITING: Show editor when row is being edited -->
            <template v-if="isInlineEditing && isRowEditing(slotProps.data) && column.editable !== false && column.data">
              <!-- Check if column is disabled for existing rows -->
              <template v-if="column.editDisabledOnEdit && !slotProps.data._isNew">
                <!-- Campo travado em edição mostra o que a LEITURA mostra: o
                     cellRenderer quando há um. Sem isto, a coluna Franquia da
                     matriz virava o ID cru ao entrar em edição — o valor do
                     campo é a chave, não o rótulo. -->
                <span v-if="column.cellRenderer" class="dt-cell-html" v-html="renderCustomCellSanitizado(column, slotProps)"></span>
                <span v-else>{{ column.data ? getDisplayValue(slotProps.data, column.data) : '' }}</span>
              </template>
              <!-- Select editor -->
              <template v-else-if="column.editType === 'select'">
                <Select
                  v-model="slotProps.data[column.data]"
                  :data-testid="cellTestId(column, slotProps.index, 'edit-select')"
                  :options="(column.editOptions as unknown[]) || []"
                  optionLabel="Name"
                  optionValue="ID"
                  :placeholder="(column.editPlaceholder as string) || ''"
                  class="w-full"
                  size="small"
                />
              </template>
              <!-- Currency editor -->
              <template v-else-if="column.editType === 'currency'">
                <InputNumber
                  v-model="slotProps.data[column.data]"
                  mode="currency"
                  currency="BRL"
                  locale="pt-BR"
                  :minFractionDigits="2"
                  :maxFractionDigits="2"
                  class="w-full"
                  size="small"
                  inputClass="p-inputtext-sm"
                />
              </template>
              <!-- Number editor -->
              <template v-else-if="column.editType === 'number'">
                <InputNumber
                  v-model="slotProps.data[column.data]"
                  :min="column.editMin"
                  :max="column.editMax"
                  class="w-full"
                  size="small"
                  inputClass="p-inputtext-sm"
                />
              </template>
              <!-- Color editor -->
              <template v-else-if="column.editType === 'color'">
                <ColorPickerInput
                  v-model="slotProps.data[column.data]"
                  :data-testid="cellTestId(column, slotProps.index, 'edit-color')"
                />
              </template>
              <!-- Text editor (default) -->
              <template v-else>
                <InputText
                  v-model="slotProps.data[column.data]"
                  class="w-full p-inputtext-sm"
                  size="small"
                />
              </template>
            </template>

            <!-- Merged Columns: master cell + stacked sub-lines via DataTableCell -->
            <div v-else-if="column.mergedColumns?.length" class="dt-merged-cell">
              <DataTableCell
                :column="column"
                :row="slotProps.data"
                :test-id-prefix="effectiveTestIdPrefix"
                :row-index="slotProps.index"
                @edit-click="$emit('edit-click', $event)"
                @button-click="$emit('button-click', $event)"
                @checkbox-change="$emit('checkbox-change', $event)"
                @toggle-change="$emit('toggle-change', $event)"
              />
              <template v-for="(sub, si) in column.mergedColumns" :key="sub.data || si">
                <DataTableCell
                  v-if="!sub.visible || sub.visible(slotProps.data)"
                  :column="sub"
                  :row="slotProps.data"
                  :is-sub-cell="true"
                  :test-id-prefix="effectiveTestIdPrefix"
                  :row-index="slotProps.index"
                  :class="resolveCellClass(sub, slotProps.data)"
                  @edit-click="$emit('edit-click', $event)"
                  @button-click="$emit('button-click', $event)"
                  @checkbox-change="$emit('checkbox-change', $event)"
                  @toggle-change="$emit('toggle-change', $event)"
                />
              </template>
            </div>

            <!-- Handle custom cellRenderer (returns HTML element or string) -->
            <div v-else-if="column.cellRenderer" class="dt-cell-html" v-html="renderCustomCellSanitizado(column, slotProps)"></div>
            
            <!-- Handle built-in renderTypes -->
            <template v-else-if="column.renderType">
              <!-- Currency -->
              <span v-if="column.renderType === 'currency' && column.data">
                {{ n(parseFloat(slotProps.data[column.data]) || 0, 'currency') }}
              </span>
              
              <!-- Number -->
              <span v-else-if="column.renderType === 'number' && column.data">
                {{ n(parseFloat(slotProps.data[column.data]) || 0, 'decimal') }}
              </span>

              <!-- Integer -->
              <span v-else-if="column.renderType === 'integer' && column.data">
                {{ n(parseFloat(slotProps.data[column.data]) || 0, 'integer') }}
              </span>

              <!-- Percentage -->
              <span v-else-if="column.renderType === 'percentage' && column.data">
                {{ n((parseFloat(slotProps.data[column.data]) || 0) / 100, 'percent') }}
              </span>
              
              <!-- Date -->
              <span v-else-if="column.renderType === 'date' && column.data">
                {{ getDisplayValue(slotProps.data, column.data, 'date') }}
              </span>
              
              <!-- DateTime -->
              <span v-else-if="column.renderType === 'datetime' && column.data">
                {{ getDisplayValue(slotProps.data, column.data, 'datetime') }}
              </span>
              
              <!-- Yes/No -->
              <span v-else-if="column.renderType === 'yes_no' && column.data">
                {{ slotProps.data[column.data] ? t('Sim') : t('Não') }}
              </span>
              
              <!-- Active/Inactive -->
              <span v-else-if="column.renderType === 'active_inactive' && column.data">
                {{ slotProps.data[column.data] ? t('Ativo') : t('Inativo') }}
              </span>
              
              <!-- Clickable ID -->
              <a
                v-else-if="column.renderType === 'clickable_id' && column.data"
                :data-testid="cellTestId(column, slotProps.index, 'edit')"
                href="javascript:void(0)"
                class="clickable-id"
                @click.prevent.stop="handleEditClick(slotProps.data[column.data])">
                {{ slotProps.data[column.data] }}
                <i class="pi pi-pencil"></i>
              </a>
              
              <!-- Clickable Button (column.data can be null) -->
              <template v-else-if="column.renderType === 'clickable_button'">
                <Button
                  v-if="!column.buttonCondition || column.buttonCondition(slotProps.data)"
                  :label="column.buttonText || 'Action'"
                  :icon="column.buttonIcon"
                  :class="column.buttonClass"
                  :data-testid="typeof column.buttonTestId === 'function' ? column.buttonTestId(slotProps.data) : column.buttonTestId"
                  :disabled="typeof column.buttonDisabled === 'function' ? column.buttonDisabled(slotProps.data) : !!column.buttonDisabled"
                  v-tooltip="typeof column.buttonTooltip === 'function' ? column.buttonTooltip(slotProps.data) : column.buttonTooltip"
                  size="small"
                  @click.stop="handleButtonClick(column.data ? slotProps.data[column.data] : null, slotProps.data, column.buttonAction)"
                />
                <span v-else></span>
              </template>

              <!-- Action Buttons - Multiple buttons in one column -->
              <template v-else-if="column.renderType === 'action_buttons' && column.buttons">
                <div class="action-buttons-cell">
                  <template v-for="(btn, btnIndex) in column.buttons" :key="btnIndex">
                    <Button
                      v-if="!btn.condition || btn.condition(slotProps.data)"
                      :data-testid="resolveButtonTestId(btn, column, slotProps)"
                      :label="btn.text"
                      :icon="btn.icon"
                      :class="btn.class"
                      :title="typeof btn.tooltip === 'function' ? btn.tooltip(slotProps.data) : btn.tooltip"
                      :disabled="typeof btn.disabled === 'function' ? btn.disabled(slotProps.data) : !!btn.disabled"
                      size="small"
                      @click.stop="handleButtonClick(null, slotProps.data, btn.action)"
                    />
                  </template>
                </div>
              </template>
              
              <!-- Copy on Click - copies cell value to clipboard when clicked -->
              <template v-else-if="column.renderType === 'copy_on_click' && column.data">
                <a
                  v-if="slotProps.data[column.data] !== null && slotProps.data[column.data] !== undefined && slotProps.data[column.data] !== ''"
                  href="javascript:void(0)"
                  class="copy-on-click"
                  :data-testid="`${column.data}-copy`"
                  :title="t('Clique para copiar')"
                  @click.prevent="handleCopyClick(column, slotProps.data[column.data], slotProps.data)">
                  {{ getDisplayValue(slotProps.data, column.data) }}
                  <i class="pi pi-copy"></i>
                </a>
                <span v-else></span>
              </template>

              <!-- Custom Clickable ID - only render if value is not empty -->
              <template v-else-if="column.renderType === 'custom_clickable_id' && column.data">
                <a
                  v-if="slotProps.data[column.data]"
                  :data-testid="cellTestId(column, slotProps.index, 'custom')"
                  href="javascript:void(0)"
                  class="clickable-id"
                  @click.prevent.stop="handleCustomClick(column, slotProps.data[column.data], slotProps.data)">
                  {{ slotProps.data[column.data] }}
                  <i class="pi pi-pencil"></i>
                </a>
                <span v-else></span>
              </template>

              <!-- Checkbox -->
              <input
                v-else-if="column.renderType === 'checkbox'"
                :data-testid="cellTestId(column, slotProps.index, 'checkbox')"
                type="checkbox"
                :class="column.checkboxClass || 'data-checkbox'"
                :checked="column.checkboxIsChecked ? column.checkboxIsChecked(slotProps.data) : false"
                @change="handleCheckboxChange(column, slotProps.data, $event)"
              />

              <!-- Toggle Switch -->
              <ToggleSwitch
                v-else-if="column.renderType === 'toggle' && column.data"
                :modelValue="slotProps.data[column.data]"
                @update:modelValue="handleToggleChange(column, slotProps.data, $event)"
                @click.stop
              />

              <!-- Badge with Tooltip -->
              <Badge
                v-else-if="column.renderType === 'badge' && column.badgeConfig"
                :data-testid="badgeTestId(column, slotProps.index)"
                :value="column.badgeConfig(slotProps.data).label"
                :severity="column.badgeConfig(slotProps.data).severity"
                v-tooltip.top="column.badgeConfig(slotProps.data).tooltip"
              />

              <!-- KPI Progress Bar -->
              <div v-else-if="column.renderType === 'kpi_progress' && column.data" class="kpi-progress-cell">
                <span class="kpi-progress-label">{{ slotProps.data[column.data] }}%</span>
                <KpiProgressBar
                  :value="parseFloat(slotProps.data[column.data]) || 0"
                  :success-threshold="column.successThreshold ?? 100"
                  :warning-threshold="column.warningThreshold ?? 80"
                />
              </div>

              <!-- Image renderType -->
              <DataTableImageCell
                v-else-if="column.renderType === 'image'"
                :column="column" :row="slotProps.data" :row-index="slotProps.index"
                :test-id-prefix="effectiveTestIdPrefix" :dense="false" />

              <!-- Default for unknown renderType -->
              <span v-else>{{ column.data ? getDisplayValue(slotProps.data, column.data) : '' }}</span>
            </template>
            
            <!-- No renderType or cellRenderer - just show value -->
            <span
              v-else
              :data-testid="column.rowTestId ? cellTestId(column, slotProps.index) : undefined"
            >{{ column.data ? getDisplayValue(slotProps.data, column.data) : '' }}</span>
          </ExplainedCell>
        </template>

        <!-- Filter Template -->
        <template #filter="{ filterModel, filterCallback }" v-if="shouldShowFilters && shouldShowFilter(column) && column.data">
          <!-- Safety check: only render if filterModel exists -->
          <template v-if="filterModel">
            <!-- Text Filter -->
            <InputText
              v-if="getFilterType(column) === 'text'"
              v-model="filterModel.value"
              type="text"
              :data-testid="testIdScope(testIdForFilter(column))"
              @input="filterCallback()"
              :placeholder="t('Buscar')"
              class="p-column-filter"
            />
            
            <!-- Number Filter -->
            <InputNumber
              v-else-if="getFilterType(column) === 'number'"
              v-model="filterModel.value"
              :data-testid="testIdScope(testIdForFilter(column))"
              @input="filterCallback()"
              :placeholder="t('Buscar')"
              class="p-column-filter"
              :useGrouping="false"
            />
            
            <!-- Date Filter -->
            <DatePicker
              v-else-if="getFilterType(column) === 'date'"
              v-model="filterModel.value"
              :data-testid="testIdScope(testIdForFilter(column))"
              @date-select="filterCallback()"
              @clear-click="filterCallback()"
              dateFormat="dd/mm/yy"
              :placeholder="t('Selecionar data')"
              class="p-column-filter"
              :showIcon="true"
            />
            
            <!-- Select Filter (Dropdown) -->
            <Select
              v-else-if="getFilterType(column) === 'select'"
              v-model="filterModel.value"
              :data-testid="testIdScope(`filter-select-${column.filterField || column.data}`)"
              @change="filterCallback()"
              :options="getFilterOptions(column)"
              :optionLabel="column.filterOptionLabel || 'label'"
              :optionValue="column.filterOptionValue || 'value'"
              :placeholder="t('Selecionar')"
              class="p-column-filter"
              :showClear="true"
            />

            <!-- Boolean Filter -->
            <Select
              v-else-if="getFilterType(column) === 'boolean'"
              v-model="filterModel.value"
              :data-testid="testIdScope(`filter-bool-${column.filterField || column.data}`)"
              @change="filterCallback()"
              :options="booleanFilterOptions"
              optionLabel="label"
              optionValue="value"
              :placeholder="t('Selecionar')"
              class="p-column-filter"
              :showClear="true"
            />

            <!-- QR Code Filter -->
            <InputQrcode
              v-else-if="getFilterType(column) === 'qrcode'"
              :elemId="testIdScope(`filter-qrcode-${column.filterField || column.data}`)"
              v-model="filterModel.value"
              @update:modelValue="filterCallback()"
              :placeholder="t('Escanear ou digitar')"
              class="p-column-filter"
            />

            <!-- Default Text Filter -->
            <InputText
              v-else
              v-model="filterModel.value"
              type="text"
              :data-testid="testIdScope(testIdForFilter(column))"
              @input="filterCallback()"
              :placeholder="t('Buscar')"
              class="p-column-filter"
            />
          </template>
          
          <!-- Debug log if filterModel is missing -->
          <div v-else style="padding: 0.5rem; font-size: 0.75rem; color: #dc3545;">
            {{ log('DataTable', 'filter-template', `filterModel undefined for column: ${column.data}`) }}
            Filter error
          </div>
        </template>
      </Column>

      <!-- Inline Editing Actions Column (only visible when a row is being edited) -->
      <Column
        v-if="isInlineEditing && editingRows.length > 0"
        :header="''"
        :style="{ width: '100px', textAlign: 'center' }">
        <template #body="slotProps">
          <template v-if="!slotProps.data._isFrozenFooter">
            <div v-if="isRowEditing(slotProps.data)" class="inline-edit-actions">
              <Button icon="pi pi-check" severity="success" text rounded size="small"
                :data-testid="testIdScope(`row-${slotProps.index}-save`)"
                :title="t('Salvar')" @click.stop="saveRowEdit(slotProps.data)" />
              <Button icon="pi pi-times" severity="secondary" text rounded size="small"
                :data-testid="testIdScope(`row-${slotProps.index}-cancel`)"
                :title="t('Cancelar')" @click.stop="cancelRowEdit(slotProps.data)" />
              <Button icon="pi pi-trash" severity="danger" text rounded size="small"
                :data-testid="testIdScope(`row-${slotProps.index}-delete`)"
                :title="t('Remover')" @click.stop="deleteRow(slotProps.data)" />
            </div>
          </template>
        </template>
      </Column>

      <!-- Checkbox Selection Column (position: last) -->
      <Column
        v-if="hasCheckboxSelection && checkboxConfig.position === 'last'"
        selectionMode="multiple"
        :frozen="checkboxConfig.frozen"
        :headerStyle="{ width: checkboxConfig.width }"
        :bodyStyle="{ textAlign: 'center' }">
      </Column>
    </PrimeDataTable>

    <!-- PrimeVue DataView (List Mode) -->
    <DataView
      v-else
      :value="sortedAndFilteredData"
      :loading="isLoadingData"
      :paginator="config.pagination === true"
      :rows="config.pageLength || 100"
      :rowsPerPageOptions="[10, 25, 50, 100]"
      :dataKey="getDataKey()"
      layout="list">
      
      <!-- Header with Filters and Sorting -->
      <template #header>
        <div v-if="shouldShowFilters || (isTableSortable && sortableColumns.length > 0) || hasCheckboxSelection" class="list-header">
          <!-- Select All Checkbox for List Mode -->
          <div v-if="hasCheckboxSelection" class="list-select-all">
            <Checkbox
              :modelValue="selectedRows?.length === displayData.filter(r => !r._isFrozenFooter).length && selectedRows?.length > 0"
              :indeterminate="selectedRows?.length > 0 && selectedRows?.length < displayData.filter(r => !r._isFrozenFooter).length"
              @update:modelValue="$event ? selectAll() : clearSelection()"
              binary
            />
            <label class="select-all-label">
              {{ selectedRows?.length > 0
                ? `${selectedRows.length} ${t('selecionado(s)')}`
                : t('Selecionar todos')
              }}
            </label>
          </div>

          <!-- Sorting Controls -->
          <div v-if="isTableSortable && sortableColumns.length > 0" class="list-sorting">
            <label class="sort-label">{{ t('Ordenar por') }}:</label>
            <div class="sort-controls">
              <Select
                v-model="sortField"
                :data-testid="testIdScope('sort-field')"
                :options="sortableColumns"
                optionLabel="label"
                optionValue="value"
                :placeholder="t('Selecionar campo')"
                class="sort-field-select"
                :showClear="true"
              />
              <Button
                v-if="sortField"
                :data-testid="testIdScope('sort-order')"
                :icon="sortOrder === 1 ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up'"
                :label="sortOrder === 1 ? t('Crescente') : t('Decrescente')"
                @click="toggleSortOrder"
                size="small"
                outlined
              />
            </div>
          </div>

          <!-- Filters -->
          <div v-if="shouldShowFilters" class="list-filters">
            <div
              v-for="column in config.columns"
              v-show="shouldShowFilter(column) && column.data"
              :key="column.data ?? undefined"
              class="list-filter-item">
              <label class="filter-label">{{ column.title }}</label>

              <!-- Text Filter -->
              <InputText
                v-if="getFilterType(column) === 'text'"
                :modelValue="getFilterTextValue(column.data ?? undefined)"
                @update:modelValue="handleFilterChange(column, $event ?? null)"
                :data-testid="testIdScope(`list-filter-text-${column.filterField || column.data}`)"
                type="text"
                :placeholder="t('Buscar')"
                class="filter-input"
              />

              <!-- Number Filter -->
              <InputNumber
                v-else-if="getFilterType(column) === 'number'"
                :modelValue="getFilterNumberValue(column.data ?? undefined)"
                @update:modelValue="handleFilterChange(column, $event ?? null)"
                :placeholder="t('Buscar')"
                class="filter-input"
                :useGrouping="false"
              />

              <!-- Date Filter -->
              <DatePicker
                v-else-if="getFilterType(column) === 'date'"
                :modelValue="getFilterDateValue(column.data ?? undefined)"
                @update:modelValue="handleFilterChange(column, ($event as Date | null))"
                dateFormat="dd/mm/yy"
                :placeholder="t('Selecionar data')"
                class="filter-input"
                :showIcon="true"
              />

              <!-- Select Filter -->
              <Select
                v-else-if="getFilterType(column) === 'select'"
                :modelValue="column.data ? filters[column.data]?.value : undefined"
                :data-testid="testIdScope(`list-filter-select-${column.filterField || column.data}`)"
                @update:modelValue="handleFilterChange(column, $event)"
                :options="getFilterOptions(column)"
                :optionLabel="column.filterOptionLabel || 'label'"
                :optionValue="column.filterOptionValue || 'value'"
                :placeholder="t('Selecionar')"
                class="filter-input"
                :showClear="true"
              />

              <!-- Boolean Filter -->
              <Select
                v-else-if="getFilterType(column) === 'boolean'"
                :modelValue="column.data ? filters[column.data]?.value : undefined"
                :data-testid="testIdScope(`list-filter-bool-${column.filterField || column.data}`)"
                @update:modelValue="handleFilterChange(column, $event)"
                :options="booleanFilterOptions"
                optionLabel="label"
                optionValue="value"
                :placeholder="t('Selecionar')"
                class="filter-input"
                :showClear="true"
              />

              <!-- QR Code Filter -->
              <InputQrcode
                v-else-if="getFilterType(column) === 'qrcode'"
                :elemId="testIdScope(`list-filter-qrcode-${column.filterField || column.data}`)"
                :modelValue="getFilterTextValue(column.data ?? undefined) ?? undefined"
                @update:modelValue="handleFilterChange(column, $event ?? null)"
                :placeholder="t('Escanear ou digitar')"
                class="filter-input"
              />

              <!-- Default Text Filter -->
              <InputText
                v-else
                :modelValue="getFilterTextValue(column.data ?? undefined)"
                @update:modelValue="handleFilterChange(column, $event ?? null)"
                type="text"
                :placeholder="t('Buscar')"
                class="filter-input"
              />
            </div>
          </div>
        </div>
      </template>
      
      <!-- List Template -->
      <template #list="slotProps">
        <div class="list-content">
          <Card
            v-for="(item, itemIdx) in slotProps.items"
            :key="item[getDataKey()]"
            class="list-card"
            :class="{ 'list-card-selected': hasCheckboxSelection && isRowSelected(item) }">
            <template #content>
              <!-- Header: primary id (left, prominent) + status badge & actions (right) -->
              <div class="list-card-header">
                <div v-if="hasCheckboxSelection" class="list-item-checkbox" @click.stop>
                  <Checkbox
                    :modelValue="isRowSelected(item)"
                    @update:modelValue="toggleRowSelection(item)"
                    binary />
                </div>
                <div class="list-card-header-primary" v-if="mobileColumnLayout.primary" @click.stop>
                  <DataTableCell
                    :column="mobileColumnLayout.primary"
                    :row="item"
                    :test-id-prefix="effectiveTestIdPrefix"
                    :row-index="itemIdx"
                    @edit-click="$emit('edit-click', $event)"
                    @button-click="$emit('button-click', $event)" />
                  <template v-if="mobileColumnLayout.primary.mergedColumns?.length">
                    <template v-for="(sub, si) in mobileColumnLayout.primary.mergedColumns" :key="sub.data || si">
                      <DataTableCell
                        v-if="!sub.visible || sub.visible(item)"
                        :column="sub"
                        :row="item"
                        :is-sub-cell="true"
                        :test-id-prefix="effectiveTestIdPrefix"
                        :row-index="itemIdx"
                        :class="resolveCellClass(sub, item)"
                        @edit-click="$emit('edit-click', $event)"
                        @button-click="$emit('button-click', $event)" />
                    </template>
                  </template>
                </div>
                <div class="list-card-header-right" v-if="mobileColumnLayout.status || mobileColumnLayout.actions" @click.stop>
                  <DataTableCell
                    v-if="mobileColumnLayout.status"
                    :column="mobileColumnLayout.status"
                    :row="item"
                    :test-id-prefix="effectiveTestIdPrefix"
                    :row-index="itemIdx"
                    @edit-click="$emit('edit-click', $event)"
                    @button-click="$emit('button-click', $event)" />
                  <DataTableCell
                    v-if="mobileColumnLayout.actions"
                    :column="mobileColumnLayout.actions"
                    :row="item"
                    :test-id-prefix="effectiveTestIdPrefix"
                    :row-index="itemIdx"
                    @edit-click="$emit('edit-click', $event)"
                    @button-click="$emit('button-click', $event)" />
                </div>
              </div>

              <!-- Body: remaining columns in a 2-column grid, label-above-value -->
              <div class="list-card-body" v-if="mobileColumnLayout.body.length">
                <div
                  v-for="column in mobileColumnLayout.body"
                  :key="column.data || column.title"
                  class="list-card-cell"
                  @click.stop>
                  <span class="field-label" v-if="column.title && column.title.trim() !== ''">{{ column.title }}</span>
                  <div class="field-value" :class="{ 'dt-image-field-value': column.renderType === 'image' }">
                    <template v-if="column.mergedColumns?.length">
                      <div class="dt-merged-cell">
                        <DataTableCell
                          :column="column"
                          :row="item"
                          :test-id-prefix="effectiveTestIdPrefix"
                          :row-index="itemIdx"
                          @edit-click="$emit('edit-click', $event)"
                          @button-click="$emit('button-click', $event)" />
                        <template v-for="(sub, si) in column.mergedColumns" :key="sub.data || si">
                          <DataTableCell
                            v-if="!sub.visible || sub.visible(item)"
                            :column="sub"
                            :row="item"
                            :is-sub-cell="true"
                            :test-id-prefix="effectiveTestIdPrefix"
                            :row-index="itemIdx"
                            :class="resolveCellClass(sub, item)"
                            @edit-click="$emit('edit-click', $event)"
                            @button-click="$emit('button-click', $event)" />
                        </template>
                      </div>
                    </template>
                    <DataTableCell
                      v-else
                      :column="column"
                      :row="item"
                      :test-id-prefix="effectiveTestIdPrefix"
                      :row-index="itemIdx"
                      @edit-click="$emit('edit-click', $event)"
                      @button-click="$emit('button-click', $event)" />
                  </div>
                </div>
              </div>
            </template>
          </Card>
        </div>
      </template>
    </DataView>

    <!-- Aggregation Summary (List Mode) -->
    <Card v-if="hasFooterAggregations && effectiveDisplayMode === 'list'" class="aggregation-summary">
      <template #content>
        <div class="summary-header">{{ config.footerLabel || t('Totais') }}</div>
        <div class="summary-content">
          <div
            v-for="column in config.columns"
            v-show="column.footerType && column.footerType !== 'none' && column.data && footerData[column.data] !== undefined"
            :key="column.data ?? undefined"
            class="summary-item">
            <strong class="summary-label" v-if="column.title && column.title.trim() !== ''">{{ column.title }}:</strong>
            <span class="summary-value">
              <span v-if="column.footerCurrency || column.renderType === 'currency'">
                {{ n(footerData[column.data!], 'currency') }}
              </span>
              <span v-else-if="column.renderType === 'number'">
                {{ n(footerData[column.data!], 'decimal') }}
              </span>
              <span v-else-if="column.renderType === 'integer'">
                {{ n(footerData[column.data!], 'integer') }}
              </span>
              <span v-else-if="column.renderType === 'percentage'">
                {{ n(footerData[column.data!] / 100, 'percent') }}
              </span>
              <span v-else>
                {{ footerData[column.data!] }}
              </span>
            </span>
          </div>
        </div>
      </template>
    </Card>

    <!-- Empty State Message -->
    <div v-if="totalRecords === 0 && !isLoadingData" class="empty-state-message">
      <i class="pi pi-inbox"></i>
      <p>{{ t('Nenhum registro encontrado') }}</p>
    </div>

    <!-- Record Count Info -->
    <div v-if="totalRecords > 0" class="record-count-info">
      <small class="text-muted">
        {{ filteredRecordCount === totalRecords
          ? `${totalRecords} ${totalRecords === 1 ? t('registro') : t('registros')}`
          : `${filteredRecordCount} / ${totalRecords} ${totalRecords === 1 ? t('registro') : t('registros')}`
        }}
      </small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { resolveScrollHeight, availableBodyHeight } from './tableHeight';
import { useI18n } from 'vue-i18n';
import { FilterMatchMode } from '@primevue/core/api';
import PrimeDataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import ToggleSwitch from 'primevue/toggleswitch';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import DatePicker from 'primevue/datepicker';
import InputQrcode from '../inputs/InputQrcode.vue';
import ColorPickerInput from '../inputs/ColorPicker.vue';
import DataView from 'primevue/dataview';
import Card from 'primevue/card';
import Checkbox from 'primevue/checkbox';
import Badge from 'primevue/badge';
import { httpService, type ApiResponse } from '../services/httpService';
import { eventBus } from '../eventBus';
import { log, error as logError } from '../utils/DebugLogger';
import { sanitizeHtmlContent } from '../utils/sanitizeHtmlContent';
import { useDateFormatter } from '../composables/useDateFormatter';
import DataTableCell from './DataTableCell.vue';
import DataTableImageCell from './DataTableImageCell.vue';
import ExplainedCell from './ExplainedCell.vue';
import InfoTip from '../ui/InfoTip.vue';
import { explanationFromText } from '../explanations/render';
import type { Explanation } from '../explanations/types';
import type { DataTableConfig, RefreshButtonConfig, CsvButtonConfig } from './types';
import { prepareExportRows, exportFilename } from './exportRows';

const props = withDefaults(defineProps<{
  /** Scope for generated data-testids. Pages should override per-instance (e.g. "clientes"). */
  testIdPrefix?: string;
  config: DataTableConfig;
  data?: unknown[];
  endpoint?: string | null;
  autoLoad?: boolean;
  filterConfig?: Record<string, unknown> | null;
  refreshButton?: RefreshButtonConfig | null;
  csvButton?: CsvButtonConfig | null;
  theme?: Record<string, unknown>;
  /** 'table' | 'list' | 'handle_user_device'. Default: 'handle_user_device' —
   *  desktop renders the regular PrimeVue DataTable, mobile collapses to
   *  the DataView/list layout. Pass 'table' to force the table view
   *  regardless of viewport. */
  displayMode?: 'table' | 'list' | 'handle_user_device';
  /** v-model:selection — two-way binding for selected rows */
  selection?: unknown[];
  /**
   * Optional per-row selection gate (PrimeVue `isDataSelectable`).
   * Return false to prevent a row from being selected or deselected.
   * Use when the page needs to constrain selection to a subset of rows
   * (e.g. rows that share the same client, or rows with a valid phone).
   */
  isDataSelectable?: (event: { data: unknown; index: number }) => boolean;
}>(), {
  displayMode: 'handle_user_device',
  autoLoad: true,
});

const emit = defineEmits<{
  (e: 'row-click', payload: any): void;
  (e: 'edit-click', id: any): void;
  (e: 'button-click', payload: any): void;
  (e: 'checkbox-change', payload: any): void;
  (e: 'toggle-change', payload: any): void;
  (e: 'refresh'): void;
  (e: 'data-loaded', data: any[]): void;
  (e: 'data-error', error: any): void;
  (e: 'csv-export'): void;
  (e: 'csv-error', error: any): void;
  (e: 'xlsx-export'): void;
  (e: 'xlsx-error', error: any): void;
  (e: 'update:selection', value: any[]): void;
  (e: 'selection-change', payload: { selection: any[]; added: any[]; removed: any[] }): void;
  (e: 'select-all', payload: any): void;
  (e: 'inline-save', payload: any): void;
  (e: 'inline-delete', payload: any): void;
  (e: 'inline-cancel'): void;
}>();

// Get i18n and date formatter
const { t, n } = useI18n();
const { formatDate, formatDateTime } = useDateFormatter();

// Local type aliases. Row and Column are intentionally loose `Record` bags
// because this is a framework-level generic table — callers (page-level)
// hold the concrete row shape and pass arbitrary column-config objects
// (with cellRenderer/footerRenderer/clickCallback functions). The project's
// migration plan (memory: project-response-contract-system-todo) is to
// thread real types in once backend response contracts settle.
type Row = Record<string, any>;
type Column = Record<string, any>;
type FilterMatchModeValue = (typeof FilterMatchMode)[keyof typeof FilterMatchMode];
// Filter value crosses through PrimeVue InputText/InputNumber/DatePicker,
// each expecting a different narrow type. The runtime only puts the
// matching primitive into a given column's filter, but the static type
// has to be the union — we keep it loose and let the v-model bindings
// in those inputs do their own coercion.
type FilterValue = string | number | boolean | Date | null;
type FilterEntry = { value: FilterValue; matchMode: FilterMatchModeValue };
type SortMetaEntry = { field: string; order: 1 | 0 | -1 };

// Reactive data
const datatableRef = ref<any>(null);
const internalData = ref<Row[]>([]);
const selectedRows = ref<Row[]>([]);
const isLoadingData = ref(false);
const filters = ref<Record<string, FilterEntry>>({});
const totalRecords = ref(0);
const filteredRecordCount = ref(0);

// Inline editing state
const editingRows = ref<Row[]>([]);
const editingRowBackups = ref(new Map<Row, Row>());

// Display mode and device detection
const isMobileDevice = ref(false);
const sortField = ref<string | null>(null);
const sortOrder = ref<1 | 0 | -1>(1); // 1 = ascending, -1 = descending
const multiSortMeta = ref<SortMetaEntry[]>([]);

// Boolean filter options
const booleanFilterOptions = [
  { label: t('Sim'), value: true },
  { label: t('Não'), value: false }
];

// Computed properties
const effectiveDisplayMode = computed(() => {
  if (props.displayMode === 'handle_user_device') {
    return isMobileDevice.value ? 'list' : 'table';
  }
  return props.displayMode;
});

const isInlineEditing = computed(() => props.config.inlineEditing === true);

// Helper to generate scoped data-testids so tests can target DataTable internals without CSS selectors.
// When testIdPrefix isn't passed, fall back to the table's tableId (sanitized) instead of the literal
// string "undefined" — without this guard, every selector becomes "undefined-row-N-foo".
const effectiveTestIdPrefix = computed<string>(() => {
  const explicit = props.testIdPrefix?.trim();
  if (explicit) return explicit;
  const fromTableId = typeof props.config?.tableId === 'string' ? props.config.tableId.trim() : '';
  return fromTableId || 'datatable';
});
const testIdScope = (suffix: string): string => `${effectiveTestIdPrefix.value}-${suffix}`;
const testIdForFilter = (column: Column): string => `filter-${column.filterField || column.data}`;
const cellTestId = (column: Column, rowIndex: number, suffix?: string): string => {
  const colKey = (column && column.data) || 'col';
  return `${effectiveTestIdPrefix.value}-row-${rowIndex}-${colKey}${suffix ? `-${suffix}` : ''}`;
};
// Badge testids live under a `-badge-` scope, NOT `-row-`: specs count rows with
// prefix selectors ([data-testid^="<prefix>-row-"]), and a badge id sharing that
// prefix makes every badge column double-count rows (broke
// whatsapp_message_templates.integration.spec.ts when the badge testid landed
// under cellTestId's -row- scope).
const badgeTestId = (column: Column, rowIndex: number): string => {
  const colKey = (column && column.data) || 'col';
  return `${effectiveTestIdPrefix.value}-badge-${rowIndex}-${colKey}`;
};

const sortableColumns = computed(() => {
  return props.config.columns
    ?.filter((col: Column) => isColumnSortable(col))
    .map((col: Column) => ({
      label: col.title,
      value: col.data
    })) || [];
});

const sortedAndFilteredData = computed(() => {
  let data = [...displayData.value];
  
  // Apply filters
  if (filters.value && Object.keys(filters.value).length > 0) {
    data = data.filter(row => {
      for (const key in filters.value) {
        const filterValue = filters.value[key]?.value;
        if (filterValue !== null && filterValue !== undefined && filterValue !== '') {
          const cellValue = row[key];
          const matchMode = filters.value[key]?.matchMode || FilterMatchMode.CONTAINS;
          
          if (matchMode === FilterMatchMode.CONTAINS) {
            const cellStr = String(cellValue || '').toLowerCase();
            const filterStr = String(filterValue).toLowerCase();
            if (!cellStr.includes(filterStr)) {
              return false;
            }
          } else if (matchMode === FilterMatchMode.EQUALS) {
            if (cellValue !== filterValue) {
              return false;
            }
          }
        }
      }
      return true;
    });
  }
  
  // Apply sorting
  if (sortField.value && sortOrder.value !== 0) {
    const field = sortField.value;
    data.sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];
      
      // Handle null/undefined
      if (aVal === null || aVal === undefined) return sortOrder.value;
      if (bVal === null || bVal === undefined) return -sortOrder.value;
      
      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * sortOrder.value;
      }
      
      // Date comparison
      if (aVal instanceof Date && bVal instanceof Date) {
        return (aVal.getTime() - bVal.getTime()) * sortOrder.value;
      }
      
      // String comparison
      return String(aVal).localeCompare(String(bVal)) * sortOrder.value;
    });
  }
  
  return data;
});

/*
 * Altura — dois contratos: MEDIDA ('fill-viewport', o padrão) e DETERMINÍSTICA
 * ('fixed' + px). A decisão e a aritmética moram em ./tableHeight (puras e
 * testadas em unidade, porque happy-dom não faz layout); aqui só se MEDE.
 */
const rootEl = ref<HTMLElement | null>(null);
const measuredBodyPx = ref<number | null>(null);
const insideDialog = ref<boolean>(false);

const heightResolution = computed(() => resolveScrollHeight({
  mode: props.config.heightMode,
  height: props.config.height,
  measuredPx: measuredBodyPx.value,
  insideDialog: insideDialog.value,
}));

const computedScrollHeight = computed<string>(() => heightResolution.value.scrollHeight);

const measureBody = (): void => {
  const root = rootEl.value;
  if (!root) return;
  const body = root.querySelector('.p-datatable-table-container') as HTMLElement | null;
  const table = root.querySelector('.p-datatable') as HTMLElement | null;
  // Escondida (aba inativa, v-show): não há o que medir agora; o ResizeObserver
  // chama de novo quando ela ganhar tamanho.
  if (!body || !table || root.offsetParent === null) return;

  let layoutGutter = 0;
  const main = root.closest('.app-main') as HTMLElement | null;
  if (main) {
    layoutGutter += parseFloat(getComputedStyle(main).paddingBottom) || 0;
    const footer = main.querySelector(':scope > .app-version-footer') as HTMLElement | null;
    if (footer) {
      const cs = getComputedStyle(footer);
      layoutGutter += footer.offsetHeight + (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
    }
  }

  const bodyRect = body.getBoundingClientRect();
  measuredBodyPx.value = availableBodyHeight({
    viewportHeight: window.innerHeight,
    bodyTopInDocument: bodyRect.top + window.scrollY,
    belowBody: table.getBoundingClientRect().bottom - bodyRect.bottom,
    layoutGutter,
  });
};

let measureFrame = 0;
const scheduleMeasure = (): void => {
  if (measureFrame) return;
  measureFrame = requestAnimationFrame(() => {
    measureFrame = 0;
    measureBody();
  });
};
let measureObserver: ResizeObserver | null = null;

const startMeasuring = (): void => {
  insideDialog.value = !!rootEl.value?.closest('.p-dialog');
  const { measures, problem } = heightResolution.value;
  if (problem) {
    logError('DataTable', 'height', problem, { tableId: props.config.tableId });
  }
  if (!measures) return;

  window.addEventListener('resize', scheduleMeasure);
  // O que está ACIMA da tabela muda de altura depois do mount (cards que
  // carregam, filtro que abre, banner global): observar o documento pega tudo
  // isso sem conhecer a página.
  if (typeof ResizeObserver !== 'undefined') {
    measureObserver = new ResizeObserver(scheduleMeasure);
    measureObserver.observe(document.body);
    if (rootEl.value) measureObserver.observe(rootEl.value);
  }
  scheduleMeasure();
};

const stopMeasuring = (): void => {
  window.removeEventListener('resize', scheduleMeasure);
  measureObserver?.disconnect();
  measureObserver = null;
  if (measureFrame) cancelAnimationFrame(measureFrame);
  measureFrame = 0;
};

const displayData = computed<Row[]>(() => {
  // props.data is unknown[] (widened prop); cast to Row[] which is Record<string,any>[]
  const data: Row[] = props.endpoint ? internalData.value : ((props.data ?? []) as Row[]);
  // If data comes from props (not endpoint), we need to process it too
  if (!props.endpoint && data.length > 0 && !data[0]?._processed) {
    return processDataForSorting(data).map((row: Row) => ({ ...row, _processed: true }));
  }
  return data;
});

const showToolbar = computed(() => {
  return (props.refreshButton && props.refreshButton.show) || props.csvButton;
});

const shouldShowFilters = computed(() => {
  // Check if filters should be shown globally
  // config.showFilters can be true (show), false (hide), or undefined (default to true)
  return props.config.showFilters !== false;
});

const isTableSortable = computed(() => {
  // Check if table should be sortable globally
  // config.sortable can be true (allow), false (disable), or undefined (default to true)
  return props.config.sortable !== false;
});

/**
 * Checkbox Selection Configuration
 * Enable with: config.checkbox = { enabled: true }
 *
 * Options:
 * - enabled: boolean - Show checkbox column (default: false)
 * - position: 'first' | 'last' - Column position (default: 'first')
 * - frozen: boolean - Freeze checkbox column (default: false)
 * - width: string - Column width (default: '3rem')
 * - headerCheckbox: boolean - Show "select all" checkbox in header (default: true)
 */
const hasCheckboxSelection = computed(() => {
  return props.config.checkbox?.enabled === true;
});

const checkboxConfig = computed(() => ({
  enabled: props.config.checkbox?.enabled || false,
  position: props.config.checkbox?.position || 'first',
  frozen: props.config.checkbox?.frozen || false,
  width: props.config.checkbox?.width || '3rem',
  headerCheckbox: props.config.checkbox?.headerCheckbox !== false
}));

const hasFooterAggregations = computed(() => {
  return props.config.columns?.some((col: Column) => col.footerType && col.footerType !== 'none');
});

const footerLabelColumn = computed(() => {
  return props.config.footerLabelColumn || props.config.columns?.[0]?.data;
});

const footerData = computed<Record<string, any>>(() => {
  if (!hasFooterAggregations.value) return {};

  const data = getFilteredData();
  const footer: Record<string, any> = {};

  props.config.columns.forEach((column: Column) => {
    if (column.data && column.footerType === 'sum') {
      footer[column.data] = data.reduce((sum: number, row: Row) => {
        return sum + (parseFloat(row[column.data]) || 0);
      }, 0);
      // Support additional field sum via footerSumField property
      if (column.footerSumField) {
        footer[column.footerSumField] = data.reduce((sum: number, row: Row) => {
          return sum + (parseFloat(row[column.footerSumField]) || 0);
        }, 0);
      }
    } else if (column.footerType === 'custom' && column.footerSumFields && Array.isArray(column.footerSumFields)) {
      // Support custom footer with multiple sum fields (footerSumFields array)
      column.footerSumFields.forEach((fieldName: string) => {
        footer[fieldName] = data.reduce((sum: number, row: Row) => {
          return sum + (parseFloat(row[fieldName]) || 0);
        }, 0);
      });
      // Also set the column.data field if present (for backwards compatibility)
      // BUT only if it's not already calculated as part of footerSumFields
      if (column.data && !column.footerSumFields.includes(column.data)) {
        footer[column.data] = footer[column.footerSumFields[0]] || 0;
      }
    } else if (column.data && column.footerType === 'count') {
      footer[column.data] = data.length;
    } else if (column.data && column.footerType === 'avg') {
      const values = data.map((row: Row) => parseFloat(row[column.data]) || 0);
      const sum = values.reduce((acc: number, val: number) => acc + val, 0);
      footer[column.data] = data.length > 0 ? sum / data.length : 0;
    } else if (column.data && column.footerType === 'unique') {
      const uniqueValues = new Set(data.map((row: Row) => row[column.data]).filter((val: unknown) => val !== null && val !== undefined));
      footer[column.data] = uniqueValues.size;
    }
  });

  return footer;
});

/**
 * Creates frozen footer rows to display aggregated values at the bottom of the table.
 * Uses PrimeVue's frozenValue prop to render a sticky footer row.
 * 
 * Supported footerType values:
 * - 'sum': Sum of all numeric values in the column
 * - 'avg': Average of all numeric values in the column
 * - 'count': Count of all rows
 * - 'unique': Count of unique values in the column
 * 
 * The footer row is marked with _isFrozenFooter flag for special rendering.
 */
const frozenFooterRows = computed<Row[]>(() => {
  if (!hasFooterAggregations.value) {
    log('DataTable', 'frozenFooterRows', 'No footer aggregations configured');
    return [];
  }

  const footerRow: Row = {
    _isFrozenFooter: true,
    [getDataKey()]: 'footer_row'
  };

  // Add footer label in the first column or designated column
  const labelColumn = footerLabelColumn.value;
  if (labelColumn) {
    footerRow[labelColumn] = props.config.footerLabel || t('Totais');
  }

  // Add aggregated values
  props.config.columns.forEach((column: Column) => {
    if (column.data && column.footerType && column.footerType !== 'none' && footerData.value[column.data] !== undefined) {
      footerRow[column.data] = footerData.value[column.data];
      // Also add footerSumField value if present
      if (column.footerSumField && footerData.value[column.footerSumField] !== undefined) {
        footerRow[column.footerSumField] = footerData.value[column.footerSumField];
      }
    }
    // Handle footerSumFields array for custom footer types
    if (column.footerType === 'custom' && column.footerSumFields && Array.isArray(column.footerSumFields)) {
      column.footerSumFields.forEach((fieldName: string) => {
        if (footerData.value[fieldName] !== undefined) {
          footerRow[fieldName] = footerData.value[fieldName];
        }
      });
    }
  });

  log('DataTable', 'frozenFooterRows', 'Created frozen footer row', footerRow);
  return [footerRow];
});

// Methods
const getDataKey = (): string => {
  // config index signature returns unknown; dataKey is a string when set
  if (props.config.dataKey) return props.config.dataKey as string;
  // Try to find an 'id' column or use first column with data
  const idColumn = props.config.columns?.find((col: Column) =>
    col.data && (col.data === 'id' || col.data.endsWith('_id'))
  );
  return idColumn?.data || props.config.columns?.find((c: Column) => c.data)?.data || 'id';
};

const getSelectionMode = (): 'multiple' | 'single' | undefined => {
  if (props.config.rowSelection === 'multiple') return 'multiple';
  if (props.config.rowSelection === 'single') return 'single';
  return undefined;
};

/**
 * The column's header explanation: `explanation`, or the legacy
 * `headerTooltip` string read as one (the alias — same ⓘ, same card).
 */
const headerExplanation = (column: Column): Explanation | null =>
  (column.explanation as Explanation | undefined) ?? explanationFromText(column.headerTooltip as string | undefined);

const isColumnSortable = (column: Column): boolean => {
  // If global sortable is false, no columns are sortable
  if (!isTableSortable.value) {
    return false;
  }

  // If column has explicit sortable property, use it
  if (column.sortable !== undefined) {
    return column.sortable;
  }

  // Image columns are not sortable by default (display only)
  if (column.renderType === 'image') {
    return false;
  }

  // Default: sortable if column has data field
  return column.data !== undefined && column.data !== null;
};

const getVirtualScrollOptions = () => {
  if (!props.config.virtualScroll) {
    return undefined;
  }
  
  // Return virtual scroll configuration
  return {
    itemSize: props.config.virtualScrollItemSize || 50,
    ...(props.config.virtualScrollOptions || {})
  };
};

const getColumnStyle = (column: Column): Record<string, string> => {
  const styles: Record<string, string> = {};
  if (column.width) {
    styles.minWidth = `${column.width}px`;
  }
  if (column.minWidth) {
    styles.minWidth = `${column.minWidth}px`;
  }
  if (column.maxWidth) {
    styles.maxWidth = `${column.maxWidth}px`;
  }
  return styles;
};

const getColumnBodyStyle = (column: Column): Record<string, string> | undefined => {
  if (!column.wrap) return undefined;
  return {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
  };
};

const getColumnDataType = (column: Column): string => {
  // Map our dataType to PrimeVue's supported types: 'text', 'numeric', 'date'
  if (column.dataType) {
    if (column.dataType === 'number') return 'numeric';
    if (column.dataType === 'date' || column.dataType === 'datetime') return 'date';
    return 'text';
  }
  
  // Auto-detect from renderType
  if (column.renderType === 'currency' || column.renderType === 'number' || column.renderType === 'percentage') {
    return 'numeric';
  }
  if (column.renderType === 'date' || column.renderType === 'datetime') {
    return 'date';
  }
  
  // Default to text
  return 'text';
};

const shouldShowFilter = (column: Column): boolean => {
  // Don't show filter if column explicitly disables it
  if (column.filterable === false) return false;
  // Don't show filter if column has no data field
  if (!column.data) return false;
  // Image columns don't show filter by default (display only)
  if (column.renderType === 'image' && column.filterable === undefined) return false;
  // Show filter if filterConfig exists and column is in it, or if no filterConfig (show all)
  if (!props.filterConfig || !props.filterConfig.columns) return true;
  // filterConfig.columns is unknown from the prop's index signature; cast for indexing
  return !!(props.filterConfig.columns as Record<string, unknown>)[column.data];
};

const getFilterType = (column: Column): string => {
  // If explicitly defined, use it
  if (column.filterType) {
    return column.filterType;
  }
  
  // Check filterConfig for this column
  if (props.filterConfig && props.filterConfig.columns && column.data) {
    // filterConfig.columns is unknown from the prop's index signature; cast for indexing
    const configuredType = (props.filterConfig.columns as Record<string, unknown>)[column.data];
    if (configuredType) {
      return String(configuredType);
    }
  }
  
  // Auto-detect from dataType
  if (column.dataType) {
    return column.dataType;
  }
  
  // Auto-detect from renderType
  if (column.renderType === 'currency' || column.renderType === 'number' || column.renderType === 'percentage') {
    return 'number';
  }
  if (column.renderType === 'date' || column.renderType === 'datetime') {
    return 'date';
  }
  if (column.renderType === 'yes_no' || column.renderType === 'active_inactive') {
    return 'boolean';
  }
  
  // Check if filterOptions are provided (select dropdown)
  if (column.filterOptions && Array.isArray(column.filterOptions)) {
    return 'select';
  }
  
  // Default to text
  return 'text';
};

/**
 * Gets or generates filter options for a column
 * For 'select' type filters, automatically extracts unique values from data if not provided
 */
const getFilterOptions = (column: Column): Array<{ label: string; value: unknown }> => {
  // If filterOptions explicitly provided, use them
  if (column.filterOptions && Array.isArray(column.filterOptions)) {
    log('DataTable', 'getFilterOptions', `Using provided options for ${column.data}:`, column.filterOptions.length);
    return column.filterOptions;
  }

  // If filter type is 'select', extract unique values from data
  const filterType = getFilterType(column);
  if (filterType === 'select' && column.data) {
    const data = displayData.value;
    log('DataTable', 'getFilterOptions', `Extracting unique values from ${data.length} rows for column ${column.data}`);
    const uniqueValues = new Set<unknown>();

    data.forEach((row: Row) => {
      const value = row[column.data];
      if (value !== null && value !== undefined && value !== '') {
        uniqueValues.add(value);
      }
    });

    // Convert to array and sort
    const options = Array.from(uniqueValues)
      .sort((a: unknown, b: unknown) => {
        // Try numeric sort first
        const numA = parseFloat(String(a));
        const numB = parseFloat(String(b));
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        // Fall back to string sort
        return String(a).localeCompare(String(b));
      })
      .map((value: unknown) => ({
        label: String(value),
        value: value
      }));

    log('DataTable', 'getFilterOptions', `Generated ${options.length} options for column ${column.data}`, options.slice(0, 5));
    return options;
  }

  log('DataTable', 'getFilterOptions', `No options generated for ${column.data} (filterType: ${filterType})`);
  return [];
};

const getFilterMatchMode = (column: Column): FilterMatchModeValue => {
  // Return appropriate match mode based on filter type
  const filterType = getFilterType(column);
  
  if (column.filterMatchMode) {
    return column.filterMatchMode;
  }
  
  switch (filterType) {
    case 'number':
    case 'date':
    case 'boolean':
    case 'select':
    case 'qrcode':
      return FilterMatchMode.EQUALS;
    case 'text':
    default:
      return FilterMatchMode.CONTAINS;
  }
};

const getDisplayValue = (rowData: Row, field: string, type: string | null = null): unknown => {
  // Otherwise, format the value based on type
  const value = rowData[field];
  if (value === null || value === undefined || value === '') return '';

  // If type is date or datetime, format it
  if (type === 'datetime' || type === 'date') {
    try {
      let dateObj = value;

      // If it's a string, parse it to Date
      if (typeof value === 'string') {
        // Parse yyyy-mm-dd or yyyy-mm-dd hh:mm:ss format
        if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
          dateObj = new Date(value);
        }
      }

      // If we have a valid Date object, format it
      if (dateObj instanceof Date && !isNaN(dateObj.getTime())) {
        if (type === 'datetime') {
          return formatDateTime(dateObj);
        } else if (type === 'date') {
          return formatDate(dateObj);
        }
      }
    } catch (e) {
      log('DataTable', 'getDisplayValue', 'Error formatting date', e);
    }
  }

  // If it's a Date object without explicit type, format it using i18n
  if (value instanceof Date) {
    try {
      // Auto-detect: if time is not midnight, show datetime
      if (value.getHours() !== 0 || value.getMinutes() !== 0 || value.getSeconds() !== 0) {
        return formatDateTime(value);
      }
      return formatDate(value);
    } catch (e) {
      log('DataTable', 'getDisplayValue', 'Error formatting date', e);
      return value.toString();
    }
  }

  // Return as-is
  return value;
};

/**
 * Renders a merged sub-column value as a plain string (not HTML).
 * Supports text, currency, number, integer, percentage, date, datetime,
 * yes_no, active_inactive. All other renderTypes fall back to raw string.
 */
const renderSubValue = (subCol: Column, rowData: Row): string => {
  if (!subCol.data) return ' ';
  const v = rowData[subCol.data];
  if (v === null || v === undefined || v === '') return ' ';

  const rt = subCol.renderType;
  if (rt === 'currency') return n(parseFloat(v) || 0, 'currency');
  if (rt === 'number') return n(parseFloat(v) || 0, 'decimal');
  if (rt === 'integer') return n(parseFloat(v) || 0, 'integer');
  if (rt === 'percentage') return n((parseFloat(v) || 0) / 100, 'percent');
  if (rt === 'date') return String(getDisplayValue(rowData, subCol.data, 'date'));
  if (rt === 'datetime') return String(getDisplayValue(rowData, subCol.data, 'datetime'));
  if (rt === 'yes_no') return v ? 'Sim' : 'Não';
  if (rt === 'active_inactive') return v ? 'Ativo' : 'Inativo';
  return String(v);
};

const resolveCellClass = (col: Column, row: Row): string => {
  if (typeof col.cellClass === 'function') return col.cellClass(row);
  if (typeof col.cellClass === 'string')   return col.cellClass;
  return '';
};

/**
 * Split columns for the mobile-list card layout:
 *  - primary: first clickable_id / custom_clickable_id column (rendered as the big card header).
 *            Falls back to the first column if none.
 *  - status:  first badge column (rendered top-right of the header).
 *  - actions: action_buttons column (rendered as the right-side action area in the header).
 *  - body:    everything else, rendered in a 2-column grid below the header.
 */
const mobileColumnLayout = computed(() => {
  const cols = (props.config?.columns ?? []) as Column[];
  const primary =
    cols.find(c => c.renderType === 'clickable_id' || c.renderType === 'custom_clickable_id') ?? cols[0];
  const status  = cols.find(c => c !== primary && c.renderType === 'badge');
  const actions = cols.find(c => c.renderType === 'action_buttons');
  const used = new Set<Column>([primary, status, actions].filter(Boolean) as Column[]);
  const body = cols.filter(c => !used.has(c));
  return { primary, status, actions, body };
});

/**
 * Tudo que sai daqui vai para um `v-html`. Passa pelo sanitizador.
 *
 * ---------------------------------------------------------------------------
 * POR QUE AQUI, E NÃO EM CADA `v-html` DO TEMPLATE
 * ---------------------------------------------------------------------------
 * São três `v-html` neste arquivo mais dois no `DataTableCell`, todos comendo o
 * retorno destas duas funções. Sanitizar na origem cobre os cinco e, mais
 * importante, cobre os 212 `cellRenderer` espalhados pelos domínios — 42 deles
 * montam a string interpolando dado de linha direto em HTML, sem escapar nada.
 *
 * O CAMINHO SEM RENDERER TAMBÉM PASSA. O `return slotProps.data[column.data]`
 * de quando não há `cellRenderer` joga o valor CRU do banco num `v-html`; é a
 * mesma injeção, só que sem ninguém ter escrito uma linha de HTML.
 *
 * ---------------------------------------------------------------------------
 * O QUE ISTO CUSTOU PARA SER POSSÍVEL
 * ---------------------------------------------------------------------------
 * Todo sanitizador remove atributo `on*`. Enquanto existisse um renderer com
 * `onclick` inline — havia um, no MessagingBottomBarContatosPendentes —, ligar
 * esta linha quebraria o botão dele em silêncio: continuaria na tela, sem
 * fazer nada. Por isso aquele consumidor virou delegação ANTES desta mudança.
 *
 * Quem for escrever renderer novo: **não use `onclick` inline**. Emita
 * `data-*` e escute por delegação no componente, como o
 * MessagingBottomBarContatosPendentes faz.
 *
 * `sanitizeHtmlContent` é o perfil de documento (preserva `img`, `table`,
 * `data-*`, `class`, `style`), não a allowlist estreita do texto rico — um
 * renderer legitimamente devolve `<span class="badge">`, ícone e tabela.
 */
const renderCustomCellSanitizado = (column: Column, slotProps: { data: Row }): unknown => {
  if (!column.cellRenderer || typeof column.cellRenderer !== 'function') {
    return sanitizeHtmlContent(String(column.data ? slotProps.data[column.data] ?? '' : ''));
  }

  try {
    const result = column.cellRenderer({
      value: column.data ? slotProps.data[column.data] : null,
      data: slotProps.data,
      node: {}
    });

    // If result is a DOM element, get its outerHTML
    if (result instanceof HTMLElement) {
      return sanitizeHtmlContent(result.outerHTML);
    }

    // If it's a string, return it
    return sanitizeHtmlContent(String(result ?? ''));
  } catch (error) {
    console.error('Error rendering custom cell:', error);
    return sanitizeHtmlContent(String(column.data ? slotProps.data[column.data] ?? '' : ''));
  }
};

/**
 * Renders a footer cell using the column's footerRenderer function
 * @param {Object} column - Column configuration with footerRenderer function
 * @param {Object} footerData - The frozen footer row data containing aggregated values
 * @returns {string} HTML string to render in the footer cell
 */
const renderFooterCellSanitizado = (column: Column, footerRowData: Row): unknown => {
  if (!column.footerRenderer || typeof column.footerRenderer !== 'function') {
    return sanitizeHtmlContent(String(column.data ? footerRowData[column.data] ?? '' : ''));
  }

  try {
    const result = column.footerRenderer({
      value: column.data ? footerRowData[column.data] : null,
      data: footerRowData,
      footerData: footerData.value
    });

    // If result is a DOM element, get its outerHTML
    if (result instanceof HTMLElement) {
      return sanitizeHtmlContent(result.outerHTML);
    }

    // If it's a string, return it
    return sanitizeHtmlContent(String(result ?? ''));
  } catch (error) {
    console.error('Error rendering footer cell:', error);
    return sanitizeHtmlContent(String(column.data ? footerRowData[column.data] ?? '' : ''));
  }
};

const handleEditClick = (value: unknown): void => {
  emit('edit-click', value);
};

const handleButtonClick = (value: unknown, data: Row | null, action: unknown): void => {
  emit('button-click', { value, data, action });
};

/**
 * Resolve the data-testid for an action_buttons button.
 * Supports both static `string` and dynamic `(row) => string` testId.
 */
const resolveButtonTestId = (btn: Record<string, unknown>, column: Column, slotProps: { data: Row; index: number }): string => {
  const tid = btn.testId;
  if (typeof tid === 'function') {
    return String(tid(slotProps.data));
  }
  return String(tid || cellTestId(column, slotProps.index, `action-${slotProps.index}`));
};

// =========================================================================
// INLINE EDITING
// =========================================================================

const isRowEditing = (rowData: Row): boolean => {
  const dataKey = getDataKey();
  const rowKey = rowData[dataKey];
  return editingRows.value.some((r: Row) => r[dataKey] === rowKey);
};

const startRowEdit = (rowData: Row): void => {
  // Backup original values
  editingRowBackups.value.set(rowData, JSON.parse(JSON.stringify(rowData)));

  // Coerce select field values to match option ID types
  props.config.columns.forEach((col: Column) => {
    if (col.editType === 'select' && col.data && col.editOptions?.length && rowData[col.data] != null) {
      const firstOption = col.editOptions[0];
      if (firstOption && typeof firstOption.ID === 'number') {
        rowData[col.data] = Number(rowData[col.data]);
      } else if (firstOption && typeof firstOption.ID === 'string') {
        rowData[col.data] = String(rowData[col.data]);
      }
    }
  });

  editingRows.value = [...editingRows.value, rowData];
};

const cancelRowEdit = (rowData: Row): void => {
  const dataKey = getDataKey();
  const rowKey = rowData[dataKey];
  const backupEntry = Array.from(editingRowBackups.value.entries()).find(([key]) => key[dataKey] === rowKey);
  if (backupEntry) {
    const backup = backupEntry[1];
    // Restore original values
    Object.keys(backup).forEach((key: string) => {
      rowData[key] = backup[key];
    });
    editingRowBackups.value.delete(backupEntry[0]);
  }
  editingRows.value = editingRows.value.filter((r: Row) => r[dataKey] !== rowKey);
  // Remove new unsaved rows
  if (rowData._isNew) {
    internalData.value = internalData.value.filter((r: Row) => r[dataKey] !== rowKey);
  }
  emit('inline-cancel');
};

const saveRowEdit = (rowData: Row): void => {
  log('DataTable', 'saveRowEdit', 'Called', { rowKey: rowData[getDataKey()] });
  const dataKey = getDataKey();
  const rowKey = rowData[dataKey];
  const backupEntry = Array.from(editingRowBackups.value.entries()).find(([key]) => key[dataKey] === rowKey);
  if (backupEntry) {
    editingRowBackups.value.delete(backupEntry[0]);
  }
  const isNew = !!rowData._isNew;
  editingRows.value = editingRows.value.filter((r: Row) => r[dataKey] !== rowKey);
  delete rowData._isNew;
  log('DataTable', 'saveRowEdit', 'Emitting inline-save', { isNew, dataKey, rowKey });
  emit('inline-save', { data: { ...rowData }, isNew });
};

const deleteRow = (rowData: Row): void => {
  emit('inline-delete', { data: { ...rowData } });
};

const addNewRow = (): void => {
  const newRow: Row = { _isNew: true };
  // Set defaults from column config
  props.config.columns.forEach((col: Column) => {
    if (col.data && col.editDefault !== undefined) {
      newRow[col.data] = col.editDefault;
    } else if (col.data) {
      newRow[col.data] = null;
    }
  });
  // Apply defaults from config
  if (props.config.newRowDefaults) {
    Object.assign(newRow, props.config.newRowDefaults);
  }
  internalData.value = [newRow, ...internalData.value];
  startRowEdit(newRow);
};

const handleCustomClick = (column: Column, value: unknown, data: Row): void => {
  if (column.clickCallback && typeof column.clickCallback === 'function') {
    column.clickCallback(value, data);
  } else {
    emit('edit-click', value);
  }
};

const handleCopyClick = async (column: Column, value: unknown, data: Row): Promise<void> => {
  const text = typeof column.copyFormatter === 'function'
    ? column.copyFormatter(value, data)
    : String(value ?? '');
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    eventBus.emit('globalMessage.success', column.copyMessage || t('Copiado para a área de transferência'));
    if (column.copyCallback && typeof column.copyCallback === 'function') {
      column.copyCallback(value, data);
    }
  } catch (err) {
    eventBus.emit('globalMessage.error', t('Erro ao copiar: ') + (err?.message || ''));
  }
};

const handleCheckboxChange = (column: Column, rowData: Row, event: Event): void => {
  if (column.checkboxCallback && typeof column.checkboxCallback === 'function') {
    column.checkboxCallback(rowData, event);
  } else {
    const checked = (event.target as HTMLInputElement | null)?.checked ?? false;
    emit('checkbox-change', { rowData, checked });
  }
};

const handleToggleChange = (column: Column, rowData: Row, newValue: boolean): void => {
  if (column.toggleCallback && typeof column.toggleCallback === 'function') {
    column.toggleCallback(rowData, newValue);
  } else {
    emit('toggle-change', { column, rowData, newValue });
  }
};

const toggleSortOrder = (): void => {
  sortOrder.value = sortOrder.value === 1 ? -1 : 1;
  log('DataTable', 'toggleSortOrder', 'Sort order changed', { field: sortField.value, order: sortOrder.value });
};

// Filter v-model accessors. The stored value is a primitive union, but
// each PrimeVue input wants its own narrow type — we read through these
// helpers so the type narrows at the boundary instead of fighting Vue's
// template typing.
const getFilterTextValue = (key: string | undefined): string | null => {
  const v = key ? filters.value[key]?.value : null;
  return typeof v === 'string' ? v : v == null ? null : String(v);
};
const getFilterNumberValue = (key: string | undefined): number | null => {
  const v = key ? filters.value[key]?.value : null;
  return typeof v === 'number' ? v : null;
};
const getFilterDateValue = (key: string | undefined): Date | null => {
  const v = key ? filters.value[key]?.value : null;
  return v instanceof Date ? v : null;
};

const handleFilterChange = (column: Column, value: FilterValue): void => {
  if (!filters.value[column.data]) {
    filters.value[column.data] = { value: null, matchMode: getFilterMatchMode(column) };
  }
  filters.value[column.data].value = value;
  log('DataTable', 'handleFilterChange', 'Filter changed', { column: column.data, value });
};

const processDataForSorting = (data: Row[]): Row[] => {
  // Process data to ensure proper data types for sorting
  return data.map((row: Row) => {
    const processedRow: Row = { ...row };

    props.config.columns?.forEach((column: Column) => {
      if (column.data && processedRow[column.data] !== null && processedRow[column.data] !== undefined) {
        const dataType = column.dataType || getColumnDataType(column);
        const originalValue = processedRow[column.data];

        // Store original value for display
        processedRow[`${column.data}_display`] = originalValue;

        // Convert numeric columns to numbers for proper sorting
        if (dataType === 'numeric' && typeof originalValue === 'string') {
          const numValue = parseFloat(originalValue);
          if (!isNaN(numValue)) {
            processedRow[column.data] = numValue;
          }
        }

        // Convert date columns to Date objects for proper sorting
        if (dataType === 'date' && typeof originalValue === 'string' && originalValue !== '') {
          try {
            // Try to parse the date string
            // Supports formats like: "2024-01-15", "15/01/2024 14:30:00", "2024-01-15 14:30:00"
            let dateObj = null;

            // Check if it's in dd/mm/yyyy format
            if (originalValue.includes('/')) {
              const parts = originalValue.split(/[\s\/:\-]/);
              if (parts.length >= 3) {
                const day = parseInt(parts[0]);
                const month = parseInt(parts[1]) - 1; // months are 0-indexed
                const year = parseInt(parts[2]);

                // Include time if present
                if (parts.length >= 5) {
                  const hours = parseInt(parts[3]) || 0;
                  const minutes = parseInt(parts[4]) || 0;
                  const seconds = parseInt(parts[5]) || 0;
                  dateObj = new Date(year, month, day, hours, minutes, seconds);
                } else {
                  dateObj = new Date(year, month, day);
                }
              }
            } else {
              // Standard ISO format or yyyy-mm-dd format
              dateObj = new Date(originalValue);
            }

            // Only set if it's a valid date
            if (dateObj && !isNaN(dateObj.getTime())) {
              processedRow[column.data] = dateObj;
            }
          } catch (e) {
            log('DataTable', 'processDataForSorting', `Error parsing date for ${column.data}:`, originalValue, e);
          }
        }
      }

      // Synthesize merged-column filter field: lowercase space-joined values
      if (column.mergedColumns?.length && column.data && !processedRow._isFrozenFooter) {
        const mergedFields = [
          column.data,
          ...column.mergedColumns
            .filter((c: Column) => !c.visible || c.visible(processedRow))
            .map((c: Column) => c.data)
            .filter(Boolean),
        ];
        const parts = mergedFields.map((f: string) => {
          const v = processedRow[f];
          if (v == null) return '';
          if (typeof v === 'object') return '';
          return String(v).toLowerCase();
        });
        processedRow[`__merged_${column.data}`] = parts.filter(Boolean).join(' ');
      }
    });

    return processedRow;
  });
};

const loadData = async (isRefresh: boolean = false): Promise<void> => {
  if (!props.endpoint) {
    log('DataTable', 'loadData', 'No endpoint provided, skipping data load');
    return;
  }

  try {
    const stack = new Error().stack;
    isLoadingData.value = true;
    log('DataTable', 'loadData', 'Loading data from endpoint', props.endpoint, { 
      isRefresh,
      callStack: stack?.split('\n').slice(1, 4).join('\n') // Log first 3 stack frames
    });
    
    const response = await httpService.get<ApiResponse<Row[]>>(props.endpoint, {
      logContext: 'DataTable',
      skipLoading: isRefresh // Skip global loading indicator on refresh
    });
    
    log('DataTable', 'loadData', 'Response received', response);

    const isSuccess = response.success;
    const data = response.dados || [];

    if (isSuccess) {
      // Process data for proper sorting and mark as processed
      internalData.value = processDataForSorting(data).map((row: Row) => ({ ...row, _processed: true }));
      totalRecords.value = internalData.value.length;
      filteredRecordCount.value = internalData.value.length;
      log('DataTable', 'loadData', 'Data loaded and processed successfully', internalData.value.length, 'rows');
      emit('data-loaded', internalData.value);
    } else {
      throw new Error(response.error || 'Failed to load data');
    }
  } catch (error) {
    console.error('Error loading data:', error);
    log('DataTable', 'loadData', 'Error', error);
    internalData.value = [];
    emit('data-error', error);
  } finally {
    isLoadingData.value = false;
  }
};

const handleRefresh = () => {
  if (props.endpoint) {
    log('DataTable', 'handleRefresh', 'Refreshing data from endpoint');
    loadData(true); // Pass true to indicate this is a refresh
  } else {
    log('DataTable', 'handleRefresh', 'Emitting refresh event to parent');
    emit('refresh');
  }
};

const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const exportXLSX = async () => {
  try {
    log('DataTable', 'exportXLSX', 'Exporting to XLSX');

    const data = getFilteredData();
    if (data.length === 0) {
      eventBus.emit('globalMessage.warn', t('Nenhum dado para exportar'));
      return;
    }

    const { headers, rows } = prepareExportRows(props.config.columns, data);
    // SheetJS is loaded on demand so it stays out of the main bundle.
    const XLSX = await import('xlsx');
    const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    // Sheet names are capped at 31 chars and cannot contain : \ / ? * [ ]
    const sheetName = (props.config.filename || 'export').replace(/[:\\/?*[\]]/g, ' ').slice(0, 31);
    XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    const bytes: ArrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    downloadBlob(blob, exportFilename(props.config.filename, 'xlsx'));

    log('DataTable', 'exportXLSX', 'XLSX exported successfully');
    emit('xlsx-export');
    eventBus.emit('globalMessage.success', t('Excel exportado com sucesso'));
  } catch (error) {
    console.error('Error exporting XLSX:', error);
    log('DataTable', 'exportXLSX', 'Error', error);
    emit('xlsx-error', error);
    eventBus.emit('globalMessage.error', t('Erro ao exportar Excel: ') + error.message);
  }
};

const exportCSV = () => {
  try {
    log('DataTable', 'exportCSV', 'Exporting to CSV');
    
    const data = getFilteredData();
    if (data.length === 0) {
      eventBus.emit('globalMessage.warn', t('Nenhum dado para exportar'));
      return;
    }
    
    const { headers, rows } = prepareExportRows(props.config.columns, data);

    // Escape commas and quotes
    const escapeCell = (value: unknown): unknown =>
      typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map((row: unknown[]) => row.map(escapeCell).join(','))
    ].join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, exportFilename(props.config.filename, 'csv'));
    
    log('DataTable', 'exportCSV', 'CSV exported successfully');
    emit('csv-export');
    eventBus.emit('globalMessage.success', t('CSV exportado com sucesso'));
  } catch (error) {
    console.error('Error exporting CSV:', error);
    log('DataTable', 'exportCSV', 'Error', error);
    emit('csv-error', error);
    eventBus.emit('globalMessage.error', t('Erro ao exportar CSV: ') + error.message);
  }
};

const onRowClick = (event: { data: Row }): void => {
  emit('row-click', event.data);
};

const onRowDblClick = (event: { data: Row }): void => {
  if (isInlineEditing.value && !isRowEditing(event.data) && !event.data._isFrozenFooter) {
    startRowEdit(event.data);
  }
};

const getRowClass = (data: Row): string | undefined => {
  // rowClass lives in the config escape-hatch index; cast to its known shape
  const rowClassFn = props.config.rowClass as ((row: Row) => string | undefined) | undefined;
  const customClass = rowClassFn ? rowClassFn(data) : '';
  if (isInlineEditing.value && !data._isFrozenFooter && !isRowEditing(data)) {
    return [customClass, 'inline-editable-row'].filter(Boolean).join(' ');
  }
  return customClass || undefined;
};

const primeDataTablePt = computed(() => {
  const rowTestId = props.config.rowTestId;
  // Modo medido: o scrollHeight do PrimeVue é só um teto; height + min-height
  // no corpo fazem a área ocupar a altura medida mesmo vazia ou com poucas linhas.
  const { fillsBody, scrollHeight } = heightResolution.value;
  const tableContainer = fillsBody ? { style: { height: scrollHeight, minHeight: scrollHeight } } : undefined;
  if (typeof rowTestId !== 'function') return tableContainer ? { tableContainer } : undefined;
  return {
    ...(tableContainer ? { tableContainer } : {}),
    bodyRow: (slot: any) => {
      // PrimeVue 4 only exposes context.index on bodyRow PT; the row data is
      // not in context. Look it up from the bound :value array via index.
      const idx = slot?.context?.index;
      if (typeof idx !== 'number') return {};
      const rows = slot?.props?.value ?? slot?.instance?.processedData;
      const data = Array.isArray(rows) ? rows[idx] : undefined;
      if (!data || data._isFrozenFooter) return {};
      const testId = rowTestId(data);
      return testId ? { 'data-testid': testId } : {};
    },
  };
});

const onRowSelect = (event: { data: Row }): void => {
  // Row selection event
  log('DataTable', 'onRowSelect', 'Row selected', event.data);
};

const onFilter = (event: { filteredValue?: Row[]; filters?: any }): void => {
  // Filter event - update filtered record count
  log('DataTable', 'onFilter', 'Filters applied', event.filteredValue?.length || 0, 'records', 'Active filters:', event.filters);
  filteredRecordCount.value = event.filteredValue?.length || 0;
};

const getFilteredData = (): Row[] => {
  // Get filtered data from the DataTable
  // If no filters applied, return all data
  const data = displayData.value;

  if (!filters.value || Object.keys(filters.value).length === 0) {
    return data;
  }

  // Apply filters manually
  return data.filter((row: Row) => {
    for (const key in filters.value) {
      const filterValue = filters.value[key]?.value;
      if (filterValue && filterValue !== '') {
        const cellValue = String(row[key] || '').toLowerCase();
        const searchValue = String(filterValue).toLowerCase();
        if (!cellValue.includes(searchValue)) {
          return false;
        }
      }
    }
    return true;
  });
};

const getSelectedRows = () => {
  return selectedRows.value ? (Array.isArray(selectedRows.value) ? selectedRows.value : [selectedRows.value]) : [];
};

const clearFilters = () => {
  filters.value = {};
  if (datatableRef.value && typeof datatableRef.value.clearFilters === 'function') {
    datatableRef.value.clearFilters();
  }
};

const refresh = () => {
  handleRefresh();
};

// Selection manipulation methods for programmatic control
const setSelection = (rows: Row[] | null): void => {
  selectedRows.value = rows || [];
  log('DataTable', 'setSelection', 'Selection set programmatically', { count: (rows || []).length });
};

const clearSelection = (): void => {
  selectedRows.value = [];
  log('DataTable', 'clearSelection', 'Selection cleared');
};

const selectAll = (): void => {
  // Get all non-frozen footer rows
  const allRows = displayData.value.filter((row: Row) => !row._isFrozenFooter);
  selectedRows.value = [...allRows];
  log('DataTable', 'selectAll', 'All rows selected', { count: allRows.length });
};

const toggleRowSelection = (row: Row): boolean => {
  const dataKey = getDataKey();
  const rowKey = row[dataKey];
  const currentSelection: Row[] = selectedRows.value || [];
  const index = currentSelection.findIndex((r: Row) => r[dataKey] === rowKey);

  if (index >= 0) {
    // Remove from selection
    selectedRows.value = currentSelection.filter((_, i: number) => i !== index);
    log('DataTable', 'toggleRowSelection', 'Row deselected', { rowKey });
    return false;
  } else {
    // Add to selection
    selectedRows.value = [...currentSelection, row];
    log('DataTable', 'toggleRowSelection', 'Row selected', { rowKey });
    return true;
  }
};

const isRowSelected = (row: Row): boolean => {
  const dataKey = getDataKey();
  const rowKey = row[dataKey];
  const currentSelection: Row[] = selectedRows.value || [];
  return currentSelection.some((r: Row) => r[dataKey] === rowKey);
};

/**
 * A chave de filtro de uma coluna — a MESMA regra usada para montar o
 * `filters` e para lê-lo no template. Estavam duplicadas, e é assim que uma
 * coluna acaba com entrada gravada sob uma chave e lida sob outra.
 */
const filterKeyOf = (column: Column): string =>
  column.mergedColumns?.length ? `__merged_${column.data}` : (column.filterField || column.data || '');

// Initialize filters
const buildFilterEntries = (): Record<string, FilterEntry> => {
  const initialFilters: Record<string, FilterEntry> = {};
  const processedFields = new Set<string>(); // Track fields to avoid duplicates

  log('DataTable', 'initializeFilters', 'Starting filter initialization', {
    totalColumns: props.config.columns?.length || 0,
    filterConfig: props.filterConfig
  });

  // Add column-specific filters
  props.config.columns?.forEach((column: Column, index: number) => {
    const shouldFilter = shouldShowFilter(column);

    log('DataTable', 'initializeFilters', `Processing column ${index}:`, {
      data: column.data,
      title: column.title,
      filterable: column.filterable,
      shouldShow: shouldFilter
    });

    // Only initialize filter if:
    // 1. Column has a data field
    // 2. Column is filterable (not explicitly disabled)
    // 3. We haven't already processed this field (avoid duplicates)
    if (column.data && shouldFilter) {
      const filterKey = filterKeyOf(column);
      if (processedFields.has(filterKey)) {
        log('DataTable', 'initializeFilters', `⊗ Skipped column ${index} (duplicate filterKey):`, column.title);
        return;
      }
      const filterType = getFilterType(column);
      const matchMode = getFilterMatchMode(column);
      initialFilters[filterKey] = { value: null, matchMode };
      processedFields.add(filterKey);
      log('DataTable', 'initializeFilters', `✓ Initialized filter for ${filterKey}:`, {
        filterType,
        matchMode
      });
    } else {
      const reason = !column.data ? 'no data field' :
                     !shouldFilter ? 'filterable=false' :
                     processedFields.has(column.data) ? 'duplicate field' : 'unknown';
      log('DataTable', 'initializeFilters', `⊗ Skipped column ${index} (${reason}):`, column.title);
    }
  });

  log('DataTable', 'initializeFilters', 'Filters initialized successfully', {
    totalFilters: Object.keys(initialFilters).length,
    filterKeys: Object.keys(initialFilters)
  });

  return initialFilters;
};

const initializeFilters = (): void => {
  filters.value = buildFilterEntries();
};

/**
 * As colunas podem MUDAR depois da montagem.
 *
 * ---------------------------------------------------------------------------
 * O BUG QUE ISTO CORRIGE: "Filter error"
 * ---------------------------------------------------------------------------
 * `initializeFilters()` rodava só no `onMounted`. Toda coluna que aparecesse
 * depois — e aparecer depois é o caso NORMAL quando a coluna depende de uma
 * permissão que chega por requisição — ficava sem entrada em `filters`, e o
 * template caía no ramo de erro, mostrando "Filter error" no lugar do campo
 * de busca.
 *
 * Foi assim na tela de chamados: a coluna Franquia só existe para o suporte, e
 * `souSuporte` só vira `true` quando a lista responde. A tabela já tinha
 * montado.
 *
 * ---------------------------------------------------------------------------
 * POR QUE PRESERVAR O VALOR DIGITADO
 * ---------------------------------------------------------------------------
 * Reatribuir `filters` inteiro apagaria o que a pessoa está digitando. E este
 * watcher dispara por qualquer recomputo das colunas — trocar de idioma
 * recria os títulos, por exemplo. Sem a preservação, a correção de um bug
 * visível criaria outro invisível: o filtro se limpando sozinho no meio da
 * busca.
 *
 * A observação é sobre o CONJUNTO DE CHAVES, não sobre o objeto: as colunas
 * costumam ser um `computed` que devolve array novo a cada leitura, e um
 * `deep` aqui rodaria à toa o tempo todo.
 */
const filterKeysAssinatura = computed(() =>
  /*
   * `props.config?` E NÃO `props.config.` — a interrogação é obrigatória aqui,
   * mesmo o tipo declarando `config` como exigido.
   *
   * Este computed é avaliado no `setup()`, no instante em que o `watch` abaixo
   * é registrado — MAIS CEDO que o `onMounted`, que é onde o resto do arquivo
   * toca em `config.columns`. Nessa janela `config` pode ainda ser `undefined`,
   * e a primeira versão daqui derrubava o componente com
   * "Cannot read properties of undefined (reading 'columns')".
   *
   * O tipo não protege: quem monta o DataTable passando `config` de um ref que
   * só é preenchido depois satisfaz o TypeScript e chega aqui com undefined. As
   * linhas 992 e 1550 já usavam `props.config?` pelo mesmo motivo.
   */
  (props.config?.columns ?? [])
    .filter((column: Column) => column.data && shouldShowFilter(column))
    .map((column: Column) => filterKeyOf(column))
    .join('|')
);

watch(filterKeysAssinatura, () => {
  const desejados = buildFilterEntries();
  const atuais = filters.value;
  const proximos: Record<string, FilterEntry> = {};

  for (const chave of Object.keys(desejados)) {
    const anterior = atuais[chave];

    // Chave que já existia mantém o valor digitado; chave nova entra zerada.
    proximos[chave] = anterior
      ? { value: anterior.value, matchMode: desejados[chave].matchMode }
      : desejados[chave];
  }

  log('DataTable', 'watch:filterKeys', 'Colunas mudaram, filtros ressincronizados', {
    antes: Object.keys(atuais),
    depois: Object.keys(proximos)
  });

  filters.value = proximos;
});

// Watchers
watch(() => props.data, (newData) => {
  if (!props.endpoint) {
    log('DataTable', 'watch:data', 'Data prop changed', {
      newLength: newData?.length || 0,
      previousTotal: totalRecords.value
    });
    totalRecords.value = newData?.length || 0;
    filteredRecordCount.value = newData?.length || 0;
  }
}, { deep: true, immediate: true });

watch(filters, () => {
  if (effectiveDisplayMode.value === 'list') {
    filteredRecordCount.value = sortedAndFilteredData.value.length;
  } else {
    const filtered = getFilteredData();
    filteredRecordCount.value = filtered.length;
  }
}, { deep: true });

watch(sortedAndFilteredData, () => {
  if (effectiveDisplayMode.value === 'list') {
    filteredRecordCount.value = sortedAndFilteredData.value.length;
  }
});

// Selection sync watchers for v-model:selection support
// Track previous selection to calculate added/removed rows
let previousSelection: Row[] = [];

// Sync external selection prop to internal selectedRows
watch(() => props.selection, (newSelection) => {
  // Only update if it's actually different (avoid loops)
  // props.selection is unknown[]; cast elements to Row[] (Record<string,any>[])
  const newArray: Row[] = Array.isArray(newSelection) ? (newSelection as Row[]) : (newSelection ? [newSelection as Row] : []);
  const currentArray: Row[] = Array.isArray(selectedRows.value) ? selectedRows.value : (selectedRows.value ? [selectedRows.value] : []);

  // Simple reference check first
  if (newArray !== currentArray) {
    // Deep comparison by dataKey
    const dataKey = getDataKey();
    const newKeys = new Set<unknown>(newArray.map((r: Row) => r[dataKey]));
    const currentKeys = new Set<unknown>(currentArray.map((r: Row) => r[dataKey]));

    const keysMatch = newKeys.size === currentKeys.size &&
      [...newKeys].every((k: unknown) => currentKeys.has(k));

    if (!keysMatch) {
      log('DataTable', 'watch:selection', 'External selection changed, syncing', {
        newCount: newArray.length,
        currentCount: currentArray.length
      });
      selectedRows.value = [...newArray];
      previousSelection = [...newArray];
    }
  }
}, { immediate: true, deep: true });

// Emit selection changes to parent for v-model and selection-change event
watch(selectedRows, (newSelection, _oldSelection) => {
  const newArray: Row[] = Array.isArray(newSelection) ? newSelection : (newSelection ? [newSelection] : []);
  const oldArray: Row[] = Array.isArray(previousSelection) ? previousSelection : (previousSelection ? [previousSelection] : []);
  const dataKey = getDataKey();

  // Emit update:selection for v-model binding
  emit('update:selection', newArray);

  // Calculate added and removed rows for selection-change event
  const oldKeys = new Set<unknown>(oldArray.map((r: Row) => r[dataKey]));
  const newKeys = new Set<unknown>(newArray.map((r: Row) => r[dataKey]));

  const added = newArray.filter((r: Row) => !oldKeys.has(r[dataKey]));
  const removed = oldArray.filter((r: Row) => !newKeys.has(r[dataKey]));

  // Only emit selection-change if there are actual changes
  if (added.length > 0 || removed.length > 0) {
    log('DataTable', 'watch:selectedRows', 'Selection changed', {
      total: newArray.length,
      added: added.length,
      removed: removed.length
    });

    emit('selection-change', {
      selection: newArray,
      added,
      removed
    });

    // Check if this was a select-all action (header checkbox)
    const allDataCount = displayData.value.length;
    if (added.length === allDataCount && oldArray.length === 0) {
      emit('select-all', { selected: true });
    } else if (removed.length === allDataCount && newArray.length === 0) {
      emit('select-all', { selected: false });
    }
  }

  // Update previous selection for next comparison
  previousSelection = [...newArray];
}, { deep: true });

// Device detection
const checkMobileDevice = () => {
  isMobileDevice.value = window.innerWidth <= 768;
  log('DataTable', 'checkMobileDevice', 'Screen width detected', {
    width: window.innerWidth,
    isMobile: isMobileDevice.value,
    displayMode: props.displayMode,
    effectiveMode: effectiveDisplayMode.value
  });
};

// Lifecycle
onMounted(() => {
  if (props.config.defaultSortField) {
    sortField.value = props.config.defaultSortField;
    if (props.config.defaultSortOrder !== undefined && props.config.defaultSortOrder !== null) {
      // Normalize string 'asc'/'desc' to numeric 1/-1 that sortOrder ref expects
      const raw = props.config.defaultSortOrder;
      sortOrder.value = raw === 'asc' ? 1 : raw === 'desc' ? -1 : (raw as 1 | -1);
    }
    if (sortField.value) {
      multiSortMeta.value = [{ field: sortField.value, order: sortOrder.value }];
    }
  }
  initializeFilters();

  // Set up device detection
  checkMobileDevice();
  window.addEventListener('resize', checkMobileDevice);
  startMeasuring();

  // Load data from endpoint if provided and autoLoad is true
  if (props.endpoint && props.autoLoad) {
    log('DataTable', 'onMounted', 'Auto-loading data from endpoint', {
      endpoint: props.endpoint,
      autoLoad: props.autoLoad
    });
    loadData();
  } else if (!props.endpoint) {
    const propData = props.data ?? [];
    log('DataTable', 'onMounted', 'Using data prop, no endpoint', {
      dataLength: propData.length
    });
    totalRecords.value = propData.length;
    filteredRecordCount.value = propData.length;
  } else {
    log('DataTable', 'onMounted', 'autoLoad is false, skipping initial load');
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobileDevice);
  stopMeasuring();
});

// Expose public API
defineExpose({
  loadData,
  refresh,
  getFilteredData,
  getSelectedRows,
  clearFilters,
  // Selection methods
  setSelection,
  clearSelection,
  selectAll,
  toggleRowSelection,
  isRowSelected,
  // Inline editing methods
  addNewRow,
  startRowEdit,
  cancelRowEdit,
  saveRowEdit
});
</script>

<style scoped>
.kpi-progress-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 80px;
}

.kpi-progress-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
}

.datatable-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.datatable-container.fill-parent-mode {
  flex: 1;
  height: 100%;
  min-height: 0; /* Important for flex containers */
}

.datatable-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

/* Action buttons cell - multiple buttons in one column */
.action-buttons-cell {
  display: flex;
  gap: 0.25rem;
  flex-wrap: nowrap;
  align-items: center;
}

.inline-edit-actions {
  display: flex;
  gap: 0.125rem;
  justify-content: center;
  align-items: center;
}

:deep(.inline-editable-row) {
  cursor: pointer;
  position: relative;
}

:deep(.inline-editable-row:hover::after) {
  content: 'Duplo clique para editar';
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--p-surface-700);
  color: var(--p-surface-0);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 10;
  pointer-events: none;
  opacity: 0.9;
}

.datatable-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: var(--app-bg-light);
  font-weight: 600;
  gap: 1rem;
}

.footer-values {
  flex: 1;
  text-align: right;
}

.empty-state-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  background-color: var(--app-bg-light);
  border: 1px solid var(--app-border-color);
  border-radius: 4px;
  color: var(--app-text-secondary-color);
}

.empty-state-message i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #adb5bd;
}

.empty-state-message p {
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
}

.record-count-info {
  padding: 8px 12px;
  text-align: right;
  background-color: var(--app-bg-light);
  border: 1px solid var(--app-border-color);
  border-top: none;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

.text-muted {
  color: var(--app-text-secondary-color);
}

/* Merged-column sub-line (replaces per-page .cell-subline over time) */
.cell-subline {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.78rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}
.dt-merged-cell { display: flex; flex-direction: column; gap: 1px; }
.dt-merged-subline {
  font-size: 0.78rem;
  color: var(--p-text-muted-color, #6b7280);
  line-height: 1.2;
}
.dt-merged-subline :deep(.p-badge) {
  font-size: 0.72rem;
}

:deep(.clickable-id) {
  color: #3b82f6;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

:deep(.clickable-id:hover) {
  text-decoration: underline;
}

:deep(.clickable-id i) {
  font-size: 0.875rem;
}

:deep(.copy-on-click) {
  color: var(--p-text-color);
  text-decoration: none;
  cursor: copy;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

:deep(.copy-on-click:hover) {
  color: #3b82f6;
}

:deep(.copy-on-click:hover i) {
  opacity: 1;
}

:deep(.copy-on-click i) {
  font-size: 0.75rem;
  opacity: 0.5;
  transition: opacity 0.15s;
}

:deep(.p-datatable) {
  font-size: 0.875rem;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: var(--app-bg-light);
  padding: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  padding: 0.5rem;
  font-size: 0.875rem;
}

:deep(.p-column-filter) {
  width: 100%;
  font-size: 0.875rem;
}

:deep(.p-datatable .p-datatable-footer) {
  background: var(--app-bg-light);
  border-top: 2px solid var(--app-border-color);
  font-weight: 600;
}

/* Frozen Footer Row Styling */
:deep(.p-datatable .p-datatable-frozen-tbody > tr) {
  background: #e9ecef !important;
  font-weight: 600;
  border-top: 2px solid #adb5bd !important;
  pointer-events: none; /* Prevent clicking/selection on frozen footer */
}

:deep(.p-datatable .p-datatable-frozen-tbody > tr > td) {
  background: #e9ecef !important;
  font-weight: 600;
  padding: 0.75rem 0.5rem !important;
  color: #212529 !important; /* Ensure text is always dark and readable */
}

:deep(.p-datatable .p-datatable-frozen-tbody > tr:hover) {
  background: #e9ecef !important;
}

/* Prevent selection styling on frozen footer rows */
:deep(.p-datatable .p-datatable-frozen-tbody > tr.p-datatable-row-selected) {
  background: #e9ecef !important;
}

:deep(.p-datatable .p-datatable-frozen-tbody > tr.p-datatable-row-selected > td) {
  background: #e9ecef !important;
  color: #212529 !important;
}

:deep(.p-datatable .p-datatable-frozen-tbody > tr:focus) {
  outline: none !important;
  background: #e9ecef !important;
}

:deep(.p-datatable .p-datatable-frozen-tbody > tr:focus > td) {
  background: #e9ecef !important;
  color: #212529 !important;
}

/* List Mode Styles */
.list-header {
  background: var(--app-bg-light);
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 0.75rem;
}

.list-select-all {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.75rem;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid var(--app-border-color);
}

.select-all-label {
  font-weight: 600;
  color: var(--app-text-color);
  font-size: 0.875rem;
  cursor: pointer;
}

.list-sorting {
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--app-border-color);
}

.sort-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--app-text-color);
  font-size: 0.875rem;
}

.sort-controls {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.sort-field-select {
  flex: 1;
  min-width: 200px;
}

.list-filters {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.list-filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-label {
  font-weight: 600;
  color: var(--app-text-secondary-color);
  font-size: 0.875rem;
}

.filter-input {
  width: 100%;
}

.list-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.list-card {
  transition: box-shadow 0.2s;
}

.list-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.list-card-selected {
  border: 2px solid #3b82f6;
  background-color: #eff6ff;
}

.list-card-selected:hover {
  border-color: #2563eb;
}

/* Checkbox styling for list mode */
.list-item-checkbox {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 0.25rem;
}

/* Reduce card body padding for list mode */
:deep(.list-card .p-card-body) {
  padding: 0.5rem !important;
}

/* Mobile-card layout: header (primary id + status/actions on right) + 2-col body */
.list-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 0.25rem 0 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 0.75rem;
}

.list-card-header-primary {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

/* Make the primary clickable id bigger and bolder — it's the card's headline */
.list-card-header-primary :deep(.clickable-id) {
  font-size: 1.5rem;
  font-weight: 700;
  color: #3b82f6;
  text-decoration: none;
}

.list-card-header-primary :deep(.cell-subline) {
  font-size: 0.78rem;
  color: var(--p-text-muted-color, #6b7280);
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}

.list-card-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* 2-column body grid */
.list-card-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}

.list-card-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.field-label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--p-text-muted-color, #9ca3af);
}

.field-value {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--p-text-color, #111827);
  word-break: break-word;
  overflow-wrap: break-word;
  line-height: 1.35;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.list-card-cell :deep(.cell-subline) {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}

/* Image renderType in list mode — fills the .field-value as a CSS-background mask.
   The image cell is positioned absolutely to bypass intermediate component
   wrappers (DataTableCell > DataTableImageCell) that would otherwise break the
   height:100% chain in the flex layout. */
.list-card-cell .field-value.dt-image-field-value {
  position: relative;
  flex: 1;
  padding: 0;
  gap: 0;
  align-self: stretch;
  overflow: hidden;
  min-height: 60px;
}
.list-card-cell .field-value.dt-image-field-value :deep(.dt-image-cell) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
}
.list-card-cell .field-value.dt-image-field-value :deep(.dt-image-cell .dt-image-thumb) {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

/* Image handling within v-html cell renderers (narrowed from plain .field-value img
   to avoid bleeding onto DataTableImageCell thumbnails) */
.field-value .dt-cell-html img {
  max-width: 100% !important;
  width: auto !important;
  height: auto !important;
  max-height: 200px !important;
  object-fit: contain !important;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: block;
  margin: 0.5rem 0;
}

/* Handle other media elements */
.field-value .dt-cell-html video,
.field-value .dt-cell-html iframe {
  max-width: 100% !important;
  width: auto !important;
  height: auto !important;
  max-height: 200px !important;
  border-radius: 4px;
  display: block;
  margin: 0.5rem 0;
}

/* Handle long text content - already defined above */

/* Special handling for different content types */
.field-value p {
  margin: 0.25rem 0;
}

.field-value ul,
.field-value ol {
  margin: 0.25rem 0;
  padding-left: 1.5rem;
}

.field-value blockquote {
  margin: 0.5rem 0;
  padding-left: 1rem;
  border-left: 3px solid var(--app-border-color);
  font-style: italic;
  color: var(--app-text-secondary-color);
}

/* Handle tables within field values */
.field-value table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.field-value table th,
.field-value table td {
  padding: 0.25rem;
  border: 1px solid var(--app-border-color);
  text-align: left;
}

.field-value table th {
  background-color: var(--app-bg-light);
  font-weight: 600;
}

.aggregation-summary {
  margin-top: 1rem;
}

:deep(.aggregation-summary .p-card-body) {
  padding: 1rem;
  background: #e9ecef;
}

.summary-header {
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.75rem;
  color: #212529;
  border-bottom: 2px solid #adb5bd;
  padding-bottom: 0.5rem;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
}

.summary-label {
  color: var(--app-text-color);
  font-size: 0.875rem;
  font-weight: 600;
}

.summary-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #212529;
}

@media (max-width: 768px) {
  .datatable-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .toolbar-left,
  .toolbar-right {
    width: 100%;
    justify-content: center;
  }
  
  :deep(.p-datatable .p-datatable-thead > tr > th),
  :deep(.p-datatable .p-datatable-tbody > tr > td) {
    font-size: 0.75rem;
    padding: 0.25rem;
  }
  
  .list-card {
    font-size: 0.875rem;
  }
  
  .field-label {
    font-size: 0.8125rem;
  }
  
  .field-value {
    font-size: 0.8125rem;
  }
  
  .field-value .dt-cell-html img {
    max-height: 150px !important;
  }
  
  .sort-field-select {
    min-width: 150px;
  }
}

:deep(.p-datatable-thead > tr > th[data-p-sorted="true"]),
:deep(.p-datatable-thead > tr > th[data-p-sorted="true"] .p-datatable-column-title),
:deep(.p-datatable-thead > tr > th[data-p-sorted="true"] .p-datatable-sort-icon),
:deep(.p-datatable-thead > tr > th[data-p-sorted="true"] .p-datatable-sort-badge) {
  color: #000;
}

/* column explanation — the <InfoTip> next to a column title */
.header-with-info {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
</style>
